import { arenaCandidates, arenaPreviewSvg, winningArena } from './ui/arenaCards';
import { characterStyle, type CharacterStyle } from './core/characters';
import { Pane } from 'tweakpane';
import { attachBots } from './sim/ai/bots';
import { createFixedStepLoop, interpolationAlpha } from './core/loop';
import { MAX_PLAYERS, PLAYER_COLORS, PLAYER_COLORS_CB } from './core/players';
import { matchWinScores } from './sim/rules/scores';
import { createProfiler, formatStats, type StageStats } from './core/perf';
import { armFighters, makeStressRng, stressEvents } from './debug/stress';
import { createMixer } from './audio/mixer';
import { createKeyboardFallback } from './input/keyboard';
import {
  consumeLatch,
  createPadEdgeTracker,
  emptyLatch,
  isPadKey,
  padIndexOf,
  padKey,
  padLabel,
  pollGamepads,
  readPad,
  type Latch,
  type PadEdges,
  type PadEdgeTracker,
} from './input/gamepad';
import {
  assignColors,
  canStartMatch,
  clearSeats,
  collectPadMap,
  createMenuState,
  cycleSeatColor,
  maxBots,
  renderMenus,
  takeOrReadySeat,
  takeMouseSeat,
  takeSeat,
  updateMenuInputHints,
} from './ui/menus';
import { activate, focusedIn, moveFocus, nudge, setFocus, defaultFocus, type NavDir } from './ui/padNav';
import { DEFAULT_MAP, loadMaps, saveMap } from './input/remap';
import { buttonLabel, createInputTracker, detectControllerFamily, type InputDevice } from './input/presentation';
import { gameplayHelp } from './input/bindings';
import type { AimMemory } from './input/aim';
import { partyOptions, partySummary } from './ui/partyOptions';
import { createParty, acceptPartySnapshot, type PartyCoordinator } from './net/party';
import { createPartyProfiles, type PartyProfiles } from './net/partyProfiles';
import { applyPartyPreset, PARTY_PRESETS, resolvePresetLevels, selectedPartyPreset } from './ui/partyPresets';
import type { PartySnapshot } from './net/protocol';
import { createFinale } from './ui/finale';
import { modeHasTeams, teamForSlot } from './sim/rules/party';
import { ARMS_RACE_LADDER } from './sim/rules/armsRace';
import type { EditorTestOptions } from './editor/view';
import './ui/party.css';
import { builtInMatchLevels, gymLevel, levelCapacity, matchLevelPool } from './levels/catalog';
import { addShake, createCamera, worldToScreen } from './render/camera';
import { buildFrame } from './render/buildFrame';
import type { RenderFrame } from './render/frame';
import { createCanvasRenderer, type Renderer } from './render/canvas/renderer';
import { gpuFailureReason, tryCreateGpuRenderer } from './render/gpu/renderer';
import { createDecalLayerPool, snapDecalToSurface, type PersistentDecalLayer } from './render/fx/decals';
import { createParticles, emitFromEvents, stepParticles, type Decal } from './render/fx/particles';
import { observeVisualEvents } from './render/feedback';
import { combatImpact, type RumbleKind } from './render/combatFeedback';
import { createCombatFeed, knockoutDetail } from './ui/combatFeed';
import { weaponIcon } from './ui/weaponIcon';
import { blankInputs, EMPTY_INPUT, type PlayerInput } from './sim/input';
import { Aim, Bot, Combat, Controller, Dead, Health, Held, HeldBy, Loose, MatchState, Player, PrevTransform, Projectile, RoundPhase, RoundState, Transform, Weapon } from './sim/traits';
import { WEAPON_BY_ID, weaponByIndex, weaponDisplayName } from './sim/weapons/defs';
import { createSimWorld, type SimHandle } from './sim/world';
import { spawnWeapon } from './sim/systems/weapons';
import { tuning } from './sim/tuning';
import { loadSettings, saveSettings, type UserSettings } from './ui/settingsStore';
import { advancePractice, createPractice, practiceLevel, practicePrompt, recoverPractice, type PracticeState } from './ui/practice';
import { loadStats, recordKos, recordMatch } from './ui/statsStore';
import { packInput, readRoomMessage, ROOM_PROTOCOL_VERSION, unpackInput, type MatchRules, type MatchSlot, type NetMessage, type PackedInput, type RoundRecovery } from './net/protocol';
import { connectRoom, RoomError, shareUrl, type RoomSession } from './net/room';
import { cleanPeerName, isRoomCode, MAX_PEERS, normalizeRoomCode, randomRoomCode } from './net/relay';
import { acceptFrames, applyRoundProgress, captureRoundProgress, checkHash, clientSteps, createOnlineMatch, HASH_EVERY_TICKS, pacingScale, type OnlineMatch } from './net/online';
import { createEditorState, fromHash, loadLibrary, type EditorState } from './editor/editor';
import { mountEditor, type EditorView } from './editor/view';
import { createRecorder } from './input/replay';
import { parseLevel, type LevelDef } from './sim/level/schema';
import { createTelemetry, privacySignalOptOut } from './telemetry/client';
import { describeDevice } from './telemetry/device';
import { createPerfAggregator } from './telemetry/perf';
import type { GameMode, MatchOutcome, MatchRole } from './telemetry/schema';

export type Game = {
  start: () => Promise<void>;
  stop: () => void;
};

type FloppyDebug = {
  rendererKind: 'gpu' | 'canvas';
  lastHash: string;
  tick: number;
  /** The room this browser is in and how its mirror of the match is doing; null when offline. */
  online: {
    code: string;
    role: 'host' | 'client';
    peerId: number;
    peers: number;
    rtt: number;
    inMatch: boolean;
    localSlot: number;
    buffered: number;
    lastHashOk: number;
    desync: boolean;
    bytesOut: number;
  } | null;
  countdown: number;
  physicsMs: number;
  gpuMs: number;
  /** Backing-store pixels and the dynamic resolution scale the renderer settled on. */
  resolution: { w: number; h: number; scale: number };
  phase: number;
  /** Screen-space player positions (CSS px) for automation and debugging. */
  players: {
    slot: number;
    color: number;
    x: number;
    y: number;
    sx: number;
    sy: number;
    hp: number;
    dead: boolean;
    blocking: boolean;
    aim: { x: number; y: number };
    weapon: string | null;
    bot: Record<string, number> | null;
  }[];
  /** Loose weapons lying around, for checking what bots are trying to fetch. */
  loose: { x: number; y: number; id: string; cooldown: number }[];
  forceLastStand: () => void;
  /** Puts a weapon straight into a fighter's hands (browser tests and manual checks of a specific gun). */
  giveWeapon: (slot: number, id: string) => void;
  freeze: (on: boolean) => void;
  stepTicks: (n: number) => void;
  /** Per-stage frame timings (ms) over the last few seconds: input, sim, fx, build, render, hud, frame. */
  perf: () => Record<string, StageStats>;
  /** rAF gaps over 12 ms seen during play since the page loaded (missed frames at 120 Hz). */
  longFrames: number;
  /**
   * The first 64 long frames: when, how long the gap was, the stage breakdown of the frame before it,
   * and the JS heap (MB, Chrome only) on either side of the gap; a drop means a major GC ran in it.
   */
  longFrameLog: { at: number; interval: number; tick: number; before: Record<string, number>; heapBefore: number; heapAfter: number }[];
  /** Per-stage cost of every frame in which the sim swapped arenas: the one frame that does a level's worth of setup. */
  rotationLog: { tick: number; level: string; stages: Record<string, number> }[];
  /** The most recent RenderFrame handed to the renderer (group/pixel accounting from the console). */
  lastFrame: () => RenderFrame | null;
  /** Stress scene: keep at least `target` particles alive and every fighter armed (0 turns it off). */
  stress: (target: number) => void;
};

type FloppyLauncher = {
  /** Start a deterministic solo match from anywhere (menu included); returns the level id used. */
  startMatch: (opts?: { level?: string; seed?: number; bots?: number; humans?: number; firstTo?: number; devices?: string[] }) => string;
  /** Drop a loose weapon into the running sim (defaults to just above player 1). */
  spawnWeapon: (id: string, x?: number, y?: number) => boolean;
  /** Put a weapon straight into a player's hands. */
  giveWeapon: (slot: number, id: string) => boolean;
  /** Every weapon id in the roster, for galleries and sweeps. */
  weaponIds: () => string[];
  /** Hold an input override for a slot for the next N sim ticks (merged over live input). */
  scriptInput: (slot: number, input: Partial<PlayerInput>, ticks: number) => void;
  /** Drop a fighter at a world position, at rest. */
  teleport: (slot: number, x: number, y: number) => boolean;
  /** Live projectile list for inspection: kind, phase, fuse, position. */
  projectiles: () => { kind: number; phase: number; fuse: number; x: number; y: number; defId: number }[];
  /** Stress scene (see FloppyDebug.stress); available before a match starts. */
  stress: (target: number) => void;
};

declare global {
  interface Window {
    __floppy?: FloppyDebug;
    __floppyLaunch?: FloppyLauncher;
  }
}

/** Used JS heap in MB from Chrome's non-standard `performance.memory`, 0 elsewhere. */
function usedHeapMb(): number {
  const mem = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
  return mem ? Math.round(mem.usedJSHeapSize / 1048576) : 0;
}

export function createGame(root: HTMLElement): Game {
  const lifecycle = new AbortController();
  const onWindow = <K extends keyof WindowEventMap>(type: K, listener: (event: WindowEventMap[K]) => void) =>
    window.addEventListener(type, listener as EventListener, { signal: lifecycle.signal });
  let stopped = false;
  let warmTimer = 0;
  let canvas = root.querySelector('#game') as HTMLCanvasElement;
  const menusEl = root.querySelector('#menus') as HTMLElement;
  // A canvas element can only ever own one context type ('2d' or 'webgpu'), so
  // each renderer is created against its own element and the winner is swapped
  // into the DOM under the same id/class.
  function adoptCanvas(next: HTMLCanvasElement) {
    if (next === canvas) return;
    next.id = canvas.id;
    next.className = canvas.className;
    canvas.replaceWith(next);
    canvas = next;
    measureCanvas();
  }
  /**
   * CSS size of the canvas. Kept current by a ResizeObserver rather than read from `clientWidth` every
   * frame: a layout read inside the frame forces a synchronous layout whenever a HUD transition has
   * dirtied the tree, which is most frames of a real fight.
   */
  const view = { w: 1280, h: 720 };
  let viewDirty = true;
  let lastDpr = window.devicePixelRatio || 1;
  function measureCanvas() {
    view.w = root.clientWidth || 1280;
    view.h = root.clientHeight || 720;
    viewDirty = true;
  }
  const sizeObserver =
    typeof ResizeObserver === 'function'
      ? new ResizeObserver((entries) => {
          for (const entry of entries) {
            const box = entry.contentBoxSize?.[0];
            if (box && entry.target === root) {
              view.w = Math.round(box.inlineSize) || 1280;
              view.h = Math.round(box.blockSize) || 720;
              viewDirty = true;
            } else if (entry.target === root) {
              measureCanvas();
            }
          }
        })
      : null;
  sizeObserver?.observe(root);
  onWindow('resize', measureCanvas);
  measureCanvas();
  const hudEl = root.querySelector('#hud') as HTMLElement;
  const settings: UserSettings = loadSettings();
  const inputPresentation = createInputTracker();
  const menus = createMenuState();
  menus.bots = settings.quickBots;
  menus.maxHp = settings.maxHp;
  menus.firstTo = settings.firstTo;
  menus.botDifficulty = settings.botDifficulty;
  menus.roundTimeLimit = settings.roundTimeLimit;
  menus.party = partyOptions(settings);
  root.classList.toggle('reduce-motion', settings.reduceShake);
  const keys = createKeyboardFallback();
  const mixer = createMixer();
  mixer.sfx = settings.sfx;
  mixer.music = settings.music;
  const loop = createFixedStepLoop(tuning.tickRate);
  const latches: Latch[] = Array.from({ length: MAX_PLAYERS }, emptyLatch);
  // Last accepted right-stick vector per pad, for the release-transient filter ({0,0} = at rest).
  const lastAim: AimMemory[] = [];
  const padEdges: PadEdgeTracker[] = [];
  /**
   * Which device drives each fighter slot in the running match ('keyboard' or 'pad:<index>'),
   * fixed when the match starts from the seats people took. Empty outside a match (and for
   * matches launched from the editor / debug hooks), in which case slot k falls back to pad k,
   * or the keyboard for slot 0 when no pad sits there.
   */
  let slotDevices: string[] = [];
  /** The last device that pressed a menu control: "Solo vs Bots" seats whoever chose it. */
  let lastMenuDevice = 'keyboard';
  let renderer: Renderer | null = null;
  let rendererKind: 'gpu' | 'canvas' = 'canvas';
  let sim: SimHandle | null = null;
  let practice: PracticeState | null = null;
  let cam = createCamera(gymLevel.bounds);
  const particles = createParticles();
  const decals: Decal[] = [];
  // Decal textures are recycled across level swaps (match and attract mode share the pool).
  const decalPool = createDecalLayerPool();
  let decalLayer: PersistentDecalLayer = decalPool.acquire(gymLevel.bounds);
  let renderedLevel: LevelDef = gymLevel;
  let raf = 0;
  let last = performance.now();
  let paused = false;
  let editor: EditorState | null = null;
  let editorView: EditorView | null = null;
  let pane: Pane | null = null;
  let extraLevels: LevelDef[] = [];
  let debugHud = false;
  let debugDraw = false;
  let freezeCam = false;
  let humanCount = 1;
  /** The relay connection while in an online room (lobby or match). */
  let room: RoomSession | null = null;
  /** Bookkeeping for the online match this browser is part of (host or client). */
  let online: OnlineMatch | null = null;
  let party: PartyCoordinator | null = null;
  let partyView: PartySnapshot | null = null;
  let profiles: PartyProfiles | null = null;
  let ownPartyCharacter = characterStyle(settings.characters[0]);
  let profileSent = false;
  let onlineCharacters: CharacterStyle[] = [];
  let pingTimer = 0;
  const NAME_KEY = 'floppy-clash.name';
  menus.lobby.name = cleanPeerName(localStorage.getItem(NAME_KEY), '');
  const slotName = (slot: number) => online?.names[slot] ?? (slot >= humanCount ? `Bot ${slot + 1}` : `P${slot + 1}`);
  // Debug: hold the sim still (rendering continues) and single-step it from the console.
  let frozen = false;
  let frozenSteps = 0;
  let recorder = createRecorder(0, 'gym');
  const finale = createFinale();
  const localRematchVotes = new Set<string>();
  const localArenaVotes = new Map<string, string>();
  let arenaVoteRenderKey = '';
  let editorTestOptions: EditorTestOptions | undefined;
  let rulesBeforeSettings = '';
  const nextRulesKey = () => JSON.stringify([settings.maxHp, settings.firstTo, settings.enabledWeapons, settings.enabledLevels, settings.botDifficulty, settings.roundTimeLimit, settings.rotation, settings.includeUserLevels, settings.showWins, partyOptions(settings)]);
  let maps = loadMaps();
  let hitStop = 0;
  let stats = loadStats();
  // Where each frame's time goes (F3 overlay, window.__floppy.perf()), and the stress scene that
  // keeps the battlefield busy for profiling: `?stress=600` or __floppy.stress(600).
  const profiler = createProfiler(240);
  let longFrames = 0;
  const longFrameLog: FloppyDebug['longFrameLog'] = [];
  const rotationLog: FloppyDebug['rotationLog'] = [];
  let rotatedThisFrame = false;
  let lastHeap = 0;
  let lastFrame: RenderFrame | null = null;
  /** Canvas CSS size, sampled once per frame. */
  // The #hud element starts visible in the markup; `null` forces the first frame to set it.
  let hudShown: boolean | null = null;
  let stressTarget = Number(new URLSearchParams(window.location.search).get('stress') ?? 0) || 0;
  const stressRng = makeStressRng();

  // -----------------------------------------------------------------------------------------------
  // Telemetry: anonymous usage and frame-pacing statistics, batched to the Worker (src/telemetry,
  // worker/telemetry.ts) and read back on /stats. Everything below is fire-and-forget; the settings
  // toggle and the browser's Global Privacy Control signal both switch it off.
  const telemetry = createTelemetry({
    enabled: settings.telemetry && !privacySignalOptOut(),
    ctx: describeDevice(),
    context: () => ({ context: tracked ? tracked.mode : 'menu', renderer: rendererKind }),
  });
  const perfAgg = createPerfAggregator();
  /** How often a perf summary goes out during a long match (endless mode never "ends"). */
  const PERF_EVERY_MS = 60_000;
  /** The match this browser is currently reporting on; null between matches. */
  let tracked: {
    mode: GameMode;
    role: MatchRole;
    level: string;
    humans: number;
    bots: number;
    firstTo: number;
    maxHp: number;
    startedAt: number;
    lastPerfAt: number;
    kills: number;
    resyncs: number;
    longFramesAt: number;
  } | null = null;
  let matchCompletionRecorded = false;
  let matchSettingsKey = '';

  function recordCompletedMatch() {
    if (!sim || matchCompletionRecorded || sim.ecs.get(RoundState)?.phase !== RoundPhase.MatchOver) return;
    matchCompletionRecorded = true;
    const ms = sim.ecs.get(MatchState);
    const mySlot = online?.localSlot ?? 0;
    const wins = matchWinScores(sim.ecs);
    const won = sim.ctx.settings.mode === 'arms-race'
      ? sim.ecs.get(RoundState)?.winner === mySlot
      : (wins[mySlot] ?? 0) >= (ms?.firstTo || 1);
    stats = recordMatch(won);
    trackMatchEnd('finished', won);
  }

  function trackMatchStart(level: LevelDef, humans: number, bots: number, firstTo: number, maxHp: number) {
    // A match started over a running one (rematch, editor playtest) ends the old one first.
    if (tracked) trackMatchEnd('restarted');
    const now = performance.now();
    const builtIn = builtInMatchLevels().some((l) => l.id === level.id) || level.id === gymLevel.id;
    tracked = {
      mode: online ? 'online' : humans === 1 && bots > 0 ? 'solo' : 'local',
      role: online ? online.role : 'offline',
      level: builtIn ? level.id : 'custom',
      humans,
      bots,
      firstTo,
      maxHp,
      startedAt: now,
      lastPerfAt: now,
      kills: 0,
      resyncs: 0,
      longFramesAt: longFrames,
    };
    perfAgg.reset();
    telemetry.match({ phase: 'start', ...matchFields(), seconds: 0, rounds: 0, kills: 0, won: false, longFrames: 0 });
  }

  /** Close the tracked match; `won` only means anything for a match the rules finished. */
  function trackMatchEnd(outcome: MatchOutcome, won = false) {
    if (!tracked) return;
    const t = tracked;
    telemetry.match({
      phase: 'end',
      ...matchFields(),
      outcome,
      seconds: Math.round((performance.now() - t.startedAt) / 1000),
      rounds: sim?.ecs.get(MatchState)?.round ?? 0,
      kills: t.kills,
      won,
      longFrames: Math.max(0, longFrames - t.longFramesAt),
    });
    flushPerf('end');
    tracked = null;
  }

  function matchFields() {
    const t = tracked!;
    return { mode: t.mode, role: t.role, level: t.level, renderer: rendererKind, humans: t.humans, bots: t.bots, firstTo: t.firstTo, maxHp: t.maxHp };
  }

  /** Ship the frame-pacing summary gathered since the last one; a second of frames is the minimum worth sending. */
  function flushPerf(reason: 'periodic' | 'end') {
    const s = perfAgg.summary();
    perfAgg.reset();
    if (!tracked) return;
    tracked.lastPerfAt = performance.now();
    if (!s || s.frames < 60) return;
    telemetry.perf({
      mode: tracked.mode,
      role: tracked.role,
      renderer: rendererKind,
      reason,
      lighting: settings.lighting,
      frames: s.frames,
      seconds: s.seconds,
      p50: s.p50,
      p95: s.p95,
      p99: s.p99,
      max: s.max,
      long12: s.long12,
      long20: s.long20,
      long50: s.long50,
      sim: s.stages.sim ?? 0,
      build: s.stages.build ?? 0,
      render: s.stages.render ?? 0,
      gpu: s.stages.gpu ?? 0,
      particles: s.stages.particles ?? 0,
      resScale: renderer?.resolutionScale ?? 1,
      players: tracked.humans + tracked.bots,
      rtt: room?.rtt ?? 0,
      desyncs: tracked.resyncs,
      heap: usedHeapMb(),
    });
  }

  // Attract mode: a bots-only brawl plays behind the menus so the title screen is never a dead panel.
  let demo: SimHandle | null = null;
  let demoCam = createCamera(gymLevel.bounds);
  const demoParticles = createParticles(2048);
  const demoDecals: Decal[] = [];
  let demoDecalLayer: PersistentDecalLayer = decalPool.acquire(gymLevel.bounds);
  let demoLevel: LevelDef = gymLevel;
  const demoLoop = createFixedStepLoop(tuning.tickRate);
  const DEMO_SCREENS = new Set(['solo', 'join', 'party', 'settings']);
  /** `--hp` values for 0..100 %, pre-stringified so a health tick allocates nothing. */
  const HP_SCALE = Array.from({ length: 101 }, (_, i) => (i / 100).toFixed(2));

  function startDemo() {
    const pool = matchLevelPool('all');
    const level = pool[Math.floor(Math.random() * pool.length)] ?? gymLevel;
    demo?.destroy();
    demo = createSimWorld({
      level,
      seed: (Math.random() * 1e9) | 0,
      settings: { playerCount: 0, bots: 4, maxHp: 100, firstTo: 0, showWins: false },
    });
    attachBots(demo.ecs, [0, 1, 2, 3]);
    demoCam = createCamera(level.bounds);
    demoParticles.clear();
    demoDecals.length = 0;
    decalPool.release(demoDecalLayer);
    demoDecalLayer = decalPool.acquire(level.bounds);
    demoLevel = level;
  }

  function stopDemo() {
    demo?.destroy();
    demo = null;
  }

  function stepDemo(dt: number) {
    if (!demo) startDemo();
    const world = demo!;
    const scale = world.ecs.get(RoundState)?.phase === RoundPhase.LastKill ? tuning.lastKillSlowmo : 1;
    const steps = demoLoop.consume(dt, scale);
    for (let i = 0; i < steps; i++) {
      const events = world.step(blankInputs(4));
      observeVisualEvents(world.ecs, events, world.ctx.tick);
      if (world.ctx.level !== demoLevel) {
        demoLevel = world.ctx.level;
        demoParticles.clear();
        demoDecals.length = 0;
        decalPool.release(demoDecalLayer);
        demoDecalLayer = decalPool.acquire(demoLevel.bounds);
      }
      emitFromEvents(events, demoParticles, demoDecals, { bloodScale: settings.reduceBlood ? 0 : 0.65 });
      demoDecalLayer.stampNew(demoDecals, (d) => (!settings.reduceBlood || d.kind === 'scorch') && snapDecalToSurface(world.ecs, d));
      demoDecals.length = 0;
      demoDecalLayer.consumed = 0;
    }
    stepParticles(demoParticles, dt);
    const frame = buildFrame(world, demoCam, interpolationAlpha(demoLoop), view.w, view.h, demoParticles, {
      colorblind: settings.colorblind,
      decalLayer: demoDecalLayer,
      reducedMotion: settings.reduceShake,
      dt: dt * scale,
      cameraDt: dt,
    });
    renderer?.render(frame);
  }

  function show() {
    // Renderer recovery can refresh menus asynchronously; the maker owns its live view.
    if (menus.screen === 'editor') return;
    if (menus.screen === 'menu' || menus.screen === 'lobby' || menus.screen === 'character') stopDemo();
    // Re-rendering replaces every node; if a pad / arrow-key cursor was on a control, put it back
    // on the same control (by id, else by label) so the cursor does not vanish on each state change.
    const padFocus = menusEl.querySelector<HTMLElement>('.pad-focus');
    const active = document.activeElement;
    const prev = padFocus ?? (active instanceof HTMLElement && menusEl.contains(active) ? active : null);
    const selection = prev instanceof HTMLInputElement && prev.selectionStart !== null
      ? [prev.selectionStart, prev.selectionEnd ?? prev.selectionStart] as const : null;
    const prevKey = prev ? (prev.id ? `#${prev.id}` : prev.textContent?.trim() ?? '') : '';
    const prevScreen = menusEl.querySelector('.menu-wrap')?.className ?? '';
    renderMenus(
      menusEl,
      menus,
      {
        homePlay: () => startHomeMatch(),
        homeCustomize: () => {
          let index = menus.seats.findIndex((seat) => seat.taken);
          if (index < 0) { takeSeat(menus.seats, lastMenuDevice, deviceLabel(lastMenuDevice)); index = menus.seats.findIndex((seat) => seat.taken); }
          openCharacter(index, 'menu');
        },
        homeInvite: () => { menus.screen = 'lobby'; menus.notice = 'Host a party, then share its invite link with friends.'; show(); },
        onlineCustomize: () => {
          if (!room || (menus.lobby.inMatch && partyView?.phase !== 'rematch')) return;
          const color = menus.lobby.peers.find((peer) => peer.me)?.color ?? 0;
          settings.characters[color] = { ...ownPartyCharacter };
          menus.characterOrigin = 'lobby'; menus.screen = 'character'; show();
        },
        preset: (id) => {
          const preset = PARTY_PRESETS.find((preset) => preset.id === id);
          if (!preset || (room && (room.role !== 'host' || menus.lobby.inMatch))) return;
          Object.assign(settings, applyPartyPreset(settings, preset.id));
          menus.party = partyOptions(settings);
          localRematchVotes.clear(); localArenaVotes.clear();
          for (const seat of menus.seats) seat.ready = false;
          saveSettings(settings);
          if (room) { party?.invalidateRules(); broadcastLobby(); }
          menus.notice = ''; show();
        },
        returnLobby: () => returnToParty(),
        local: () => {
          clearSeats(menus.seats);
          menus.screen = 'join';
          menus.bots = 0;
          show();
        },
        bots: () => {
          // Seat whoever pressed the button (pad or keyboard), readied, with a full table of bots:
          // one more press of Start and the match is on.
          clearSeats(menus.seats);
          const seat = takeOrReadySeat(menus.seats, lastMenuDevice, deviceLabel(lastMenuDevice));
          if (seat) seat.ready = true;
          menus.screen = 'solo';
          menus.bots = settings.quickBots;
          menus.firstTo = settings.firstTo === 10 ? 10 : 5;
          menus.botDifficulty = settings.botDifficulty;
          show();
        },
        quickStart: () => startQuickMatch(),
        partyRules: () => openPartyRules(),
        partyChanged: () => {
          for (const seat of menus.seats) seat.ready = false;
          if (room?.role === 'host') { party?.invalidateRules(); broadcastParty(); broadcastLobby(); }
        },
        practice: () => startPractice(),
        joinSeat: (slot) => {
          takeMouseSeat(menus.seats, Number(slot));
          menus.bots = Math.min(menus.bots, maxBots(menus.seats));
          show();
        },
        readySeat: (slot) => {
          const seat = menus.seats[Number(slot)];
          if (seat?.taken) seat.ready = !seat.ready;
          show();
        },
        colorSeat: (payload) => {
          const [slot, dir] = (payload ?? '').split(':').map(Number);
          const seat = menus.seats[slot!];
          if (seat?.taken) cycleSeatColor(seat, dir === -1 ? -1 : 1, menus.seats);
          show();
        },
        characterSeat: (slot) => openCharacter(Number(slot)),
        characterBack: () => { menus.screen = menus.characterOrigin; show(); },
        characterChanged: (value) => {
          const color = menus.characterOrigin === 'lobby' ? menus.lobby.peers.find((peer) => peer.me)?.color ?? 0 : menus.seats[menus.characterSeat]?.color ?? 0;
          const style = characterStyle(JSON.parse(value ?? '{}'), color);
          settings.characters[color] = style;
          if (menus.characterOrigin === 'lobby' && room) {
            ownPartyCharacter = style;
            settings.characters[0] = { ...style };
            const me = menus.lobby.peers.find((peer) => peer.me);
            if (me) me.character = { ...style };
            if (room.role === 'host') { profiles?.setOwn(style); broadcastLobby(); }
            else room.send('host', { t: 'profile', character: style });
          }
          saveSettings(settings);
        },
        cycleBots: () => {
          const cap = room ? Math.max(0, MAX_PEERS - room.peers.size) : Math.min(MAX_PLAYERS - 1, maxBots(menus.seats));
          const min = !room && menus.screen === 'menu' && menus.seats.filter((seat) => seat.taken).length <= 1 ? 1 : 0;
          menus.bots = cap > 0 ? menus.bots >= cap ? min : menus.bots + 1 : 0;
          for (const seat of menus.seats) seat.ready = false;
          if (!room) { settings.quickBots = menus.bots; saveSettings(settings); }
          if (room?.role === 'host') { party?.invalidateRules(); broadcastParty(); broadcastLobby(); }
          show();
        },
        online: () => {
          menus.screen = 'lobby';
          show();
        },
        editor: () => openEditor(),
        settings: () => openSettings(),
        start: () => startIfReady(),
        back: () => backOut(),
        save: () => {
          if (nextRulesKey() !== rulesBeforeSettings) {
            localRematchVotes.clear(); localArenaVotes.clear();
            for (const seat of menus.seats) seat.ready = false;
          }
          saveSettings(settings);
          lastAim.length = 0;
          particles.clear();
          demoParticles.clear();
          if (settings.reduceBlood) { decalLayer.clear(); demoDecalLayer?.clear(); }
          menus.maxHp = settings.maxHp;
          menus.firstTo = settings.firstTo;
          menus.botDifficulty = settings.botDifficulty;
          menus.roundTimeLimit = settings.roundTimeLimit;
          if (!room) menus.party = partyOptions(settings);
          if (room?.role === 'host' && nextRulesKey() !== rulesBeforeSettings) {
            const before = JSON.parse(rulesBeforeSettings);
            if (before[0] !== settings.maxHp) menus.lobby.maxHp = settings.maxHp;
            if (before[1] !== settings.firstTo) menus.lobby.firstTo = settings.firstTo;
            const updated = partyOptions(settings);
            for (const key of Object.keys(updated) as (keyof typeof updated)[]) {
              if (JSON.stringify(before[9]?.[key]) !== JSON.stringify(updated[key])) menus.party = { ...menus.party, [key]: updated[key] };
            }
            party?.invalidateRules();
            broadcastParty();
            broadcastLobby();
          }
          mixer.sfx = settings.sfx;
          mixer.music = settings.music;
          mixer.resume();
          root.classList.toggle('reduce-motion', settings.reduceShake);
          void applyVideoSettings();
          telemetry.setEnabled(settings.telemetry && !privacySignalOptOut());
          closeSettings();
        },
        saveRemap: () => {
          const { padId, map } = collectPadMap(menusEl);
          saveMap(padId, map);
          maps = loadMaps();
          menus.notice = `Saved remap for ${padId}`;
          show();
        },
        resume: () => setPaused(false),
        edit: () => returnToEditor(),
        quit: () => quitToMenu(),
        host: () => void joinOnline('host'),
        joinRoom: () => void joinOnline('client'),
        leave: () => leaveRoom(),
        startOnline: () => void startOnlineMatch(),
        readyOnline: () => toggleOnlineReady(),
        voteOnline: () => voteRematch(),
        lobbyChanged: () => {
          localStorage.setItem(NAME_KEY, menus.lobby.name);
          if (room?.role === 'host') { party?.invalidateRules(); broadcastParty(); broadcastLobby(); }
        },
        copyLink: () => {
          const url = menus.lobby.shareUrl;
          if (!url) return;
          const fallback = () => { menus.notice = `Invite: ${url}`; show(); };
          if (!navigator.clipboard) { fallback(); return; }
          void navigator.clipboard.writeText(url).then(
            () => {
              menus.notice = 'Invite link copied.';
              show();
            },
            fallback,
          );
        },
        chat: (text?: string) => {
          if (!text || !room) return;
          const from = menus.lobby.name || 'you';
          pushChat(`${from}: ${text}`);
          const msg: NetMessage = { t: 'chat', from, text };
          if (room.role === 'host') room.send('all', msg);
          else room.send('host', msg);
          show();
        },
      },
      settings,
      maps,
      stats,
      inputPresentation.device,
    );
    if (prevKey && menusEl.querySelector('.menu-wrap')?.className === prevScreen) {
      const again = prevKey.startsWith('#')
        ? menusEl.querySelector<HTMLElement>(prevKey)
        : [...menusEl.querySelectorAll<HTMLElement>('button')].find((b) => b.textContent?.trim() === prevKey);
      if (again) {
        if (padFocus) setFocus(menusEl, again);
        else again.focus({ preventScroll: true });
        if (selection && again instanceof HTMLInputElement) again.setSelectionRange(...selection);
      }
    }
    syncControlHints(true);
  }

  let hintRevision = -1;
  let hintGlyphs = settings.controllerGlyphs;
  function syncControlHints(force = false) {
    if (!force && hintRevision === inputPresentation.revision && hintGlyphs === settings.controllerGlyphs) return;
    hintRevision = inputPresentation.revision;
    hintGlyphs = settings.controllerGlyphs;
    const device = inputPresentation.device;
    root.dataset.inputDevice = device.kind;
    root.dataset.controllerFamily = device.kind === 'gamepad' ? device.family : '';
    updateMenuInputHints(menusEl, device, settings, maps);
    updatePracticeCoach();
    hud.edit.title = device.kind === 'gamepad' ? `${buttonLabel(8, device, settings.controllerGlyphs)} · Return to your draft` : 'Return to your draft';
    const map = device.kind === 'gamepad' ? maps[device.id] : undefined;
    hud.pause.title = `${gameplayHelp('pause', device, settings.controllerGlyphs, map)} · Pause`;
  }

  function useKeyboardHints() {
    lastMenuDevice = 'keyboard';
    if (inputPresentation.useKeyboard()) syncControlHints();
  }

  function openEditor() {
    stopDemo();
    if (!editor) {
      editor = createEditorState();
      const hash = location.hash.startsWith('#l=') ? location.hash.slice(3) : '';
      if (hash) {
        const loaded = fromHash(hash);
        if (loaded) editor.level = loaded;
      }
    }
    editorView?.dispose();
    keys.clear();
    latches.forEach(consumeLatch);
    menus.screen = 'editor';
    editorView = mountEditor(menusEl, editor, {
      getInput: () => inputPresentation.device,
      onKeyboard: useKeyboardHints,
      getGlyphs: () => settings.controllerGlyphs,
      playtest: (level, device?: string, options?: EditorTestOptions) => startEditorTest(level, device, options),
      back: () => {
        editorView?.dispose();
        editorView = null;
        menus.screen = 'menu';
        show();
      },
    });
  }

  function startEditorTest(level: LevelDef, device = lastMenuDevice, options?: EditorTestOptions) {
    editorTestOptions = options;
    const requested = Number.isFinite(options?.fighters) ? options!.fighters! : 2;
    const fighters = Math.max(2, Math.min(levelCapacity(level), Math.floor(requested)));
    startSim(structuredClone(level), { playerCount: 1, bots: fighters - 1, firstTo: 1, devices: [device], editorPlaytest: true });
    if (options?.fromCursor && sim) {
      const player = sim.players()[0];
      const body = player && sim.ctx.bodies.get(player);
      if (player && body) {
        const { x, y } = options.fromCursor;
        body.setPosition({ x, y });
        body.setLinearVelocity({ x: 0, y: 0 });
        player.set(Transform, { x, y, angle: 0 });
        player.set(PrevTransform, { x, y, angle: 0 });
      }
    }
  }

  function returnToEditor() {
    if (!menus.editorPlaytest || !editor) return;
    trackMatchEnd('quit');
    mixer.stopMusic();
    sim?.destroy();
    sim = null;
    practice = null;
    paused = false;
    menus.editorPlaytest = false;
    slotDevices = [];
    resetBanner();
    openEditor();
  }

  function openSettings() {
    rulesBeforeSettings = nextRulesKey();
    menus.settingsOrigin = sim ? 'pause' : room ? 'lobby' : menus.screen === 'solo' || menus.screen === 'join' ? menus.screen : 'menu';
    if (sim && !paused) setPaused(true);
    keys.clear();
    latches.forEach(consumeLatch);
    menus.screen = 'settings';
    show();
  }

  function openCharacter(index: number, origin: 'menu' | 'join' = 'join') {
    if (!menus.seats[index]?.taken) return;
    menus.characterSeat = index;
    menus.characterOrigin = origin;
    menus.screen = 'character';
    show();
  }

  function openPartyRules() {
    if (!['menu', 'solo', 'join', 'lobby'].includes(menus.screen) || (room && room.role !== 'host')) return;
    menus.partyOrigin = menus.screen as 'menu' | 'solo' | 'join' | 'lobby';
    menus.screen = 'party';
    show();
  }

  function closeSettings() {
    keys.clear();
    latches.forEach(consumeLatch);
    menus.screen = menus.settingsOrigin === 'pause' ? sim ? 'pause' : 'menu' : menus.settingsOrigin;
    if (menus.screen === 'pause' && missingSeatedPad()) menus.screen = 'disconnect';
    show();
  }

  function missingSeatedPad() {
    const connected = new Set(pollGamepads().filter(Boolean).map((pad) => padKey(pad!)));
    return slotDevices.some((device) => isPadKey(device) && !connected.has(device));
  }

  let matchStarting = false;
  function startHomeMatch() {
    const humans = menus.seats.filter((seat) => seat.taken);
    if (humans.length > 1) {
      if (!humans.every((seat) => seat.ready)) { menus.screen = 'join'; show(); return; }
      void beginMatch();
      return;
    }
    if (!humans.length) takeSeat(menus.seats, lastMenuDevice, deviceLabel(lastMenuDevice));
    startQuickMatch();
  }

  function startQuickMatch() {
    // Quick Match has one human: the device that starts it owns that seat, even if
    // the setup screen was opened using a different device.
    const seat = menus.seats.find((candidate) => candidate.taken);
    if (seat) {
      seat.padId = lastMenuDevice;
      seat.name = deviceLabel(lastMenuDevice);
      seat.ready = true;
    }
    settings.firstTo = menus.firstTo;
    settings.botDifficulty = menus.botDifficulty;
    settings.quickBots = menus.bots;
    Object.assign(settings, partyOptions(menus.party));
    saveSettings(settings);
    void beginMatch();
  }

  async function beginMatch() {
    // Start and Enter both land here; the level library load is async, so guard against a
    // double press spawning two sims.
    if (matchStarting) return;
    matchStarting = true;
    try {
      Object.assign(settings, partyOptions(menus.party));
      saveSettings(settings);
      const taken = menus.seats.filter((s) => s.taken);
      const humans = Math.max(1, taken.length);
      const bots = Math.min(menus.bots, maxBots(menus.seats));
      extraLevels = settings.includeUserLevels ? await loadLibrary().catch(() => []) : [];
      const pool = matchLevelPool(resolvePresetLevels(settings, humans + bots), extraLevels, humans + bots, settings.mode);
      const level =
        settings.rotation === 'ordered'
          ? (pool[0] ?? gymLevel)
          : (pool[Math.floor(Math.random() * pool.length)] ?? gymLevel);
      startSim(level, {
        playerCount: humans,
        bots,
        maxHp: settings.maxHp,
        firstTo: settings.firstTo,
        devices: taken.length ? taken.map((s) => s.padId) : ['keyboard'],
        colors: assignColors(menus.seats, humans + bots),
      });
    } finally {
      matchStarting = false;
    }
  }

  function startIfReady() {
    if (!canStartMatch(menus.seats)) {
      menus.notice = 'Join and ready each player, then start the match.';
      show();
      return;
    }
    void beginMatch();
  }

  function deviceLabel(device: string): string {
    if (device === 'keyboard') return 'Keyboard';
    const pad = pollGamepads()[padIndexOf(device)];
    return pad ? padLabel(pad) : 'Pad';
  }

  function matchIsOver(): boolean {
    return !!sim && menus.screen === 'play' && sim.ecs.get(RoundState)?.phase === RoundPhase.MatchOver;
  }

  function leaveFinishedMatch() {
    if (menus.editorPlaytest) returnToEditor();
    else returnToParty();
  }

  /** Same seats, same rules, fresh arena and seed: the "play again" the match-over card promises. */
  function rematch() {
    if (!sim) return;
    finale.skip();
    if (menus.editorPlaytest && editor) {
      startEditorTest(editor.level, slotDevices[0] ?? lastMenuDevice, editorTestOptions);
      return;
    }
    if (online) {
      voteRematch();
      return;
    }
    if (humanCount > 1 && sim.ecs.get(RoundState)?.phase === RoundPhase.MatchOver) {
      const voters = slotDevices.slice(0, humanCount);
      if (!voters.includes(lastMenuDevice)) return;
      if (localRematchVotes.has(lastMenuDevice)) localRematchVotes.delete(lastMenuDevice);
      else localRematchVotes.add(lastMenuDevice);
      if (!voters.every((device) => localRematchVotes.has(device))) return;
    }
    const bots = sim.players().filter((p) => p.has(Bot)).length;
    const colors = sim.players().map((p) => p.get(Player)?.color ?? 0);
    const rules = nextRulesKey() !== matchSettingsKey ? settings : sim.ctx.settings;
    const pool = matchLevelPool(resolvePresetLevels(rules, humanCount + bots), extraLevels, humanCount + bots, rules.mode);
    const winner = winningArena(rematchArenaChoices(), localArenaVotes.values());
    const level = pool.find((level) => level.id === winner) ?? (settings.rotation === 'ordered' ? (pool[0] ?? gymLevel) : (pool[Math.floor(Math.random() * pool.length)] ?? gymLevel));
    startSim(level, { playerCount: humanCount, bots, devices: slotDevices, colors, rules });
  }

  type StartOpts = {
    playerCount: number;
    bots: number;
    maxHp?: number;
    firstTo?: number;
    seed?: number;
    /** Input device per human slot; omitted for editor / debug launches (slot k <- pad k, or the keyboard). */
    devices?: string[];
    colors?: number[];
    /** Online: the host's rules, so every mirror of the match is created identically. */
    rules?: MatchRules;
    practice?: boolean;
    editorPlaytest?: boolean;
    /** A coordinated sync repair continues the logical match and its completion accounting. */
    recovery?: boolean;
  };

  function startSim(level: LevelDef, opts: StartOpts) {
    const players = Math.max(0, Math.min(MAX_PLAYERS, Math.floor(opts.playerCount)));
    opts = { ...opts, playerCount: players, bots: Math.max(0, Math.min(MAX_PLAYERS - players, Math.floor(opts.bots))) };
    editorView?.dispose();
    editorView = null;
    menus.editorPlaytest = !!opts.editorPlaytest;
    practice = null;
    finale.reset();
    localRematchVotes.clear();
    localArenaVotes.clear();
    arenaVoteRenderKey = '';
    lastAim.length = 0;
    keys.clear();
    latches.forEach(consumeLatch);
    mixer.resume();
    mixer.startMusic();
    const seed = opts.seed ?? (Math.random() * 1e9) | 0;
    humanCount = opts.playerCount;
    slotDevices = opts.devices ? opts.devices.slice(0, Math.max(opts.playerCount, opts.devices.length)) : [];
    stopDemo();
    sim?.destroy();
    const rules = opts.rules;
    sim = createSimWorld({
      level,
      seed,
      extraLevels: opts.editorPlaytest ? [level] : extraLevels,
      settings: {
        ...partyOptions(opts.practice || opts.editorPlaytest ? {} : rules ?? settings),
        playerCount: opts.playerCount,
        bots: opts.bots,
        maxHp: opts.maxHp ?? rules?.maxHp ?? settings.maxHp,
        firstTo: opts.firstTo ?? rules?.firstTo ?? settings.firstTo,
        botDifficulty: rules?.botDifficulty ?? settings.botDifficulty,
        roundTimeLimit: opts.practice ? 0 : rules?.roundTimeLimit ?? settings.roundTimeLimit,
        enabledWeapons: opts.practice ? [] : rules?.enabledWeapons ?? settings.enabledWeapons,
        enabledLevels: opts.editorPlaytest ? [level.id] : resolvePresetLevels(rules ?? settings, opts.playerCount + opts.bots),
        rotation: rules?.rotation ?? settings.rotation,
        showWins: rules?.showWins ?? settings.showWins,
        colors: opts.colors,
      },
    });
    level = sim.ctx.level;
    recorder = createRecorder(seed, level.id);
    if (opts.bots > 0) {
      const botSlots: number[] = [];
      sim.ecs.query(Player).updateEach(([p]) => {
        if (p.slot >= opts.playerCount) botSlots.push(p.slot);
      });
      attachBots(sim.ecs, botSlots);
    }
    cam = createCamera(level.bounds);
    particles.clear();
    decals.length = 0;
    decalPool.release(decalLayer);
    decalLayer = decalPool.acquire(level.bounds);
    renderedLevel = level;
    resetBanner();
    paused = false;
    hitStop = 0;
    menus.screen = 'play';
    show();
    const ms = sim.ecs.get(MatchState);
    if (!opts.recovery) {
      matchCompletionRecorded = false;
      matchSettingsKey = nextRulesKey();
      trackMatchStart(level, opts.playerCount, opts.bots, ms?.firstTo ?? opts.firstTo ?? settings.firstTo, ms?.maxHp ?? opts.maxHp ?? settings.maxHp);
    }
  }

  function startPractice() {
    startSim(practiceLevel, { playerCount: 1, bots: 0, firstTo: 0, practice: true, devices: [lastMenuDevice] });
    const player = sim!.players()[0]!;
    practice = createPractice(player.get(Transform)!.x);
    updatePracticeCoach();
  }

  function updatePracticeCoach() {
    hud.practice.classList.toggle('hidden', !practice);
    if (!practice) return;
    // Practice instructions describe the fighter's controls; menu activity alone
    // must not advertise controls belonging to somebody else's seat.
    const assigned = slotDevices[0] ?? 'keyboard';
    const pad = isPadKey(assigned) ? pollGamepads()[padIndexOf(assigned)] : undefined;
    const device: InputDevice = practice.step === 5 ? inputPresentation.device : isPadKey(assigned)
      ? { kind: 'gamepad', index: padIndexOf(assigned), id: pad?.id ?? '', family: detectControllerFamily(pad?.id ?? '') }
      : { kind: 'keyboard' };
    const prompt = practicePrompt(practice.step, device, settings.controllerGlyphs, device.kind === 'gamepad' ? maps[device.id] : undefined);
    hud.practice.dataset.step = String(practice.step);
    hud.practiceTitle.textContent = `${practice.step < 5 ? `${practice.step + 1} / 5 · ` : ''}${prompt.title}`;
    hud.practiceBody.textContent = prompt.body;
    hud.practiceStart.classList.toggle('hidden', practice.step < 5);
  }

  // Debug/automation: per-slot input overrides that hold for a number of sim ticks, so browser
  // tests can drive a fighter deterministically regardless of frame pacing.
  const scripted: { input: Partial<PlayerInput>; ticks: number }[] = [];
  function applyScriptedInputs(live: PlayerInput[]): PlayerInput[] {
    if (!scripted.some((s) => s && s.ticks > 0)) return live;
    return live.map((input, slot) => {
      const s = scripted[slot];
      if (!s || s.ticks <= 0) return input;
      s.ticks -= 1;
      return { ...input, ...s.input };
    });
  }

  // Inspection surface for browser tests and the console. Built once; the live fields are getters
  // that read the sim on demand, so an idle page does not pay for entity queries every frame.
  const debugApi: FloppyDebug = {
    get rendererKind() {
      return rendererKind;
    },
    get lastHash() {
      return sim?.hash() ?? '';
    },
    get tick() {
      return sim?.ctx.tick ?? 0;
    },
    get online() {
      if (!room) return null;
      return {
        code: room.code,
        role: room.role,
        peerId: room.id,
        peers: room.peers.size,
        rtt: room.rtt,
        inMatch: !!online,
        localSlot: online?.localSlot ?? -1,
        buffered: online?.inbox.length ?? 0,
        lastHashOk: online?.lastHashOk ?? 0,
        desync: online?.desync ?? false,
        bytesOut: room.bytesOut,
      };
    },
    get countdown() {
      return lastFrame?.hud.countdown ?? 0;
    },
    get physicsMs() {
      return sim?.ctx.lastPhysicsMs ?? 0;
    },
    get gpuMs() {
      return renderer?.lastGpuMs ?? 0;
    },
    get resolution() {
      return { w: renderer?.canvas.width ?? 0, h: renderer?.canvas.height ?? 0, scale: renderer?.resolutionScale ?? 1 };
    },
    get phase() {
      return sim?.ecs.get(RoundState)?.phase ?? 0;
    },
    get players() {
      if (!sim) return [];
      const ecs = sim.ecs;
      const viewW = canvas.clientWidth || 1280;
      const viewH = canvas.clientHeight || 720;
      return sim.players().map((e) => {
        const t = e.get(Transform) ?? { x: 0, y: 0, angle: 0 };
        const s = worldToScreen(cam, t.x, t.y, viewW, viewH);
        let weapon: string | null = null;
        for (const w of ecs.query(Weapon, Held)) {
          if (w.targetFor(HeldBy) === e) weapon = weaponByIndex(w.get(Weapon)!.defId).id;
        }
        const bot = e.get(Bot);
        return {
          slot: e.get(Player)?.slot ?? 0,
          color: e.get(Player)?.color ?? 0,
          x: t.x,
          y: t.y,
          sx: s.x,
          sy: s.y,
          hp: e.get(Health)?.hp ?? 0,
          dead: e.has(Dead),
          blocking: e.get(Combat)?.blocking ?? false,
          aim: { x: e.get(Aim)?.x ?? 1, y: e.get(Aim)?.y ?? 0 },
          weapon,
          bot: bot ? { mode: bot.mode, target: bot.target, surf: bot.surf, detour: bot.detour, timer: bot.timer } : null,
        };
      });
    },
    get loose() {
      if (!sim) return [];
      return sim.ecs.query(Weapon, Loose, Transform).map((w) => {
        const wt = w.get(Transform)!;
        const wep = w.get(Weapon)!;
        return { x: wt.x, y: wt.y, id: weaponByIndex(wep.defId).id, cooldown: wep.pickupCooldown };
      });
    },
    forceLastStand: () => {
      if (!sim) return;
      sim.players().forEach((p) => {
        const slot = p.get(Player)?.slot ?? 0;
        if (slot !== 0) p.set(Health, { hp: 0, maxHp: p.get(Health)?.maxHp ?? 100 });
      });
    },
    giveWeapon: (slot, id) => {
      if (!sim) return;
      const p = sim.players().find((e) => e.get(Player)?.slot === slot);
      const t = p?.get(Transform);
      if (!p || !t) return;
      for (const w of sim.ecs.query(Weapon, Held)) {
        if (w.targetFor(HeldBy) === p) sim.ctx.pendingDestroy.push(w);
      }
      spawnWeapon(sim.ecs, id, t.x, t.y, false).add(Held(), HeldBy(p));
    },
    freeze: (on) => {
      frozen = on;
      frozenSteps = 0;
    },
    stepTicks: (n) => {
      frozenSteps += Math.max(0, n | 0);
    },
    perf: () => profiler.stats(),
    get longFrames() {
      return longFrames;
    },
    longFrameLog,
    rotationLog,
    lastFrame: () => lastFrame,
    stress: (target) => {
      stressTarget = Math.max(0, target | 0);
    },
  };
  window.__floppy = debugApi;

  window.__floppyLaunch = {
    scriptInput: (slot, input, ticks) => {
      scripted[slot] = { input, ticks: Math.max(0, ticks | 0) };
    },
    startMatch: (opts = {}) => {
      const count = Math.min(MAX_PLAYERS, (opts.humans ?? 1) + (opts.bots ?? 3));
      const pool = matchLevelPool(settings.enabledLevels, extraLevels, count, settings.mode);
      const level = pool.find((l) => l.id === opts.level) ?? (opts.level === 'gym' && levelCapacity(gymLevel) >= count ? gymLevel : null) ?? pool[0] ?? gymLevel;
      startSim(level, { playerCount: opts.humans ?? 1, bots: opts.bots ?? 3, seed: opts.seed ?? 1, firstTo: opts.firstTo, devices: opts.devices });
      return sim!.ctx.level.id;
    },
    spawnWeapon: (id, x, y) => {
      if (!sim || !WEAPON_BY_ID.has(id)) return false;
      const p1 = sim.players()[0]?.get(Transform);
      spawnWeapon(sim.ecs, id, x ?? (p1?.x ?? 0) + 1, y ?? (p1?.y ?? 0) + 1);
      return true;
    },
    giveWeapon: (slot, id) => {
      if (!sim || !WEAPON_BY_ID.has(id)) return false;
      const owner = sim.players().find((e) => e.get(Player)?.slot === slot);
      const at = owner?.get(Transform);
      if (!owner || !at) return false;
      for (const w of sim.ecs.query(Weapon, Held)) {
        if (w.targetFor(HeldBy) === owner) sim.ctx.pendingDestroy.push(w);
      }
      const gun = spawnWeapon(sim.ecs, id, at.x, at.y, false);
      gun.add(Held(), HeldBy(owner));
      sim.ctx.bodies.get(gun)?.setActive(false);
      return true;
    },
    weaponIds: () => [...WEAPON_BY_ID.keys()],
    teleport: (slot, x, y) => {
      if (!sim) return false;
      const who = sim.players().find((e) => e.get(Player)?.slot === slot);
      const body = who ? sim.ctx.bodies.get(who) : undefined;
      if (!who || !body) return false;
      body.setPosition({ x, y });
      body.setLinearVelocity({ x: 0, y: 0 });
      who.set(Transform, { x, y, angle: 0 });
      return true;
    },
    projectiles: () => {
      const out: { kind: number; phase: number; fuse: number; x: number; y: number; defId: number }[] = [];
      if (!sim) return out;
      sim.ecs.query(Projectile).updateEach(([p]) => {
        out.push({ kind: p.kind, phase: p.phase, fuse: p.fuse, x: p.x, y: p.y, defId: p.defId });
      });
      return out;
    },
    stress: (target) => {
      stressTarget = Math.max(0, target | 0);
    },
  };

  function padMapFor(id: string) {
    return maps[id];
  }

  /** Which device steers a fighter slot right now (see {@link slotDevices}). */
  function deviceForSlot(slot: number, pads: (Gamepad | null)[]): string {
    // Seats decided: a pad that never joined steers nobody.
    if (slotDevices.length) return slotDevices[slot] ?? '';
    const pad = pads[slot];
    if (pad) return padKey(pad);
    return slot === 0 ? 'keyboard' : '';
  }

  function setPaused(on: boolean) {
    if (!sim || paused === on) return;
    keys.clear();
    latches.forEach(consumeLatch);
    online?.sender?.neutral(performance.now());
    paused = on;
    if (on) mixer.stopMusic();
    else { mixer.resume(); mixer.startMusic(); }
    menus.screen = on ? 'pause' : 'play';
    show();
  }

  function quitToMenu() {
    if (online) {
      // Online the match belongs to the room: the host ends it for everyone, a client just steps out
      // of it and waits in the lobby for the next one.
      if (online.role === 'host') endOnlineMatch('the host ended it');
      else {
        online.sender?.neutral(performance.now());
        endOnlineLocal('you left the match', 'left');
      }
      return;
    }
    trackMatchEnd('quit');
    mixer.stopMusic();
    sim?.destroy();
    sim = null;
    practice = null;
    paused = false;
    menus.editorPlaytest = false;
    slotDevices = [];
    resetBanner();
    menus.screen = 'menu';
    show();
  }

  // -----------------------------------------------------------------------------------------------
  // Online play. The host's browser runs the authoritative sim and every client mirrors it by
  // stepping the exact inputs the host stepped (src/net/online.ts). The relay — one Durable Object
  // per room code — only forwards messages; it knows nothing about the game.

  function pushChat(line: string) {
    menus.chat.push(line);
    if (menus.chat.length > 60) menus.chat.splice(0, menus.chat.length - 60);
  }

  /** Fighter slots for the room: the host first, then everyone else in join order. */
  function roomSlots(): MatchSlot[] {
    if (!room) return [];
    const hostId = room.hostId;
    const peers = [...room.peers.values()].sort((a, b) => (a.id === hostId ? -1 : b.id === hostId ? 1 : a.id - b.id));
    profiles?.syncRoster(peers.slice(0, MAX_PEERS).map((p, color) => ({ id: p.id, color })));
    return peers.slice(0, MAX_PEERS).map((p, i) => ({ peer: p.id, name: p.name, color: i,
      character: profiles?.get(p.id, i) ?? menus.lobby.peers.find((peer) => peer.id === p.id)?.character ?? characterStyle(undefined, i),
    }));
  }

  function refreshLobbyPeers(peers?: { id: number; name: string; slot: number; color: number; character?: CharacterStyle }[]) {
    const lobby = menus.lobby;
    if (!room) {
      lobby.peers = [];
      return;
    }
    const me = room.id;
    const hostId = room.hostId;
    const list = peers ?? roomSlots().map((s, i) => ({ id: s.peer!, name: s.name, slot: i, color: s.color, character: s.character }));
    lobby.peers = list.map((p) => ({ ...p, host: p.id === hostId, me: p.id === me }));
  }

  function nextOnlineRules(): MatchRules {
    return {
      ...partyOptions(menus.party), maxHp: menus.lobby.maxHp, firstTo: menus.lobby.firstTo,
      showWins: settings.showWins, rotation: settings.rotation,
      enabledWeapons: settings.enabledWeapons, enabledLevels: settings.enabledLevels,
      botDifficulty: settings.botDifficulty, roundTimeLimit: settings.roundTimeLimit,
    };
  }

  /** Host: tell the room what the lobby looks like (peers, rules, whether a match is running). */
  function broadcastLobby() {
    if (!room || room.role !== 'host') return;
    refreshLobbyPeers();
    const lobby = menus.lobby;
    lobby.bots = Math.min(menus.bots, Math.max(0, MAX_PEERS - room.peers.size));
    lobby.inMatch = !!sim && !!online;
    lobby.partyRules = partyOptions(menus.party);
    lobby.rules = nextOnlineRules();
    lobby.presetId = selectedPartyPreset(lobby.rules);
    room.send('all', {
      t: 'lobby',
      peers: lobby.peers.map(({ id, name, slot, color, character }) => ({ id, name, slot, color, character })),
      maxHp: lobby.maxHp,
      firstTo: lobby.firstTo,
      bots: lobby.bots,
      inMatch: lobby.inMatch,
      partyRules: lobby.partyRules,
      rules: lobby.rules,
      presetId: selectedPartyPreset(lobby.rules),
    });
    party?.syncRoster(room.peers.keys());
    broadcastParty();
    if (menus.screen === 'lobby') show();
  }

  function broadcastParty() {
    if (!room || room.role !== 'host' || !party) return;
    if (party.snapshot().phase === 'rematch') party.setArenaChoices(rematchArenaChoices());
    partyView = party.snapshot();
    menus.lobby.party = partyView;
    room.send('all', partyView);
    if (menus.screen === 'lobby') show();
  }

  function toggleOnlineReady() {
    if (!room || !partyView || partyView.phase !== 'lobby') return;
    const ready = !partyView.ready.includes(room.id);
    if (room.role === 'host') {
      party?.setReady(room.id, ready);
      broadcastParty();
    } else room.send('host', { t: 'ready', epoch: partyView.epoch, ready });
  }

  function rematchArenaChoices(): string[] {
    if (!sim) return [];
    const count = online && room ? Math.min(MAX_PLAYERS, room.peers.size + menus.bots) : sim.players().length;
    const rules = nextRulesKey() !== matchSettingsKey ? settings : sim.ctx.settings;
    return arenaCandidates(matchLevelPool(resolvePresetLevels(rules, count), extraLevels, count, rules.mode), sim.ctx.level.id);
  }

  function arenaVoteChoices(): string[] {
    return online ? partyView?.arenaChoices ?? [] : rematchArenaChoices();
  }

  function arenaVoter(device: string): string | undefined {
    if (online) return room ? String(room.id) : undefined;
    const pads = pollGamepads();
    const voters = Array.from({ length: humanCount }, (_, slot) => deviceForSlot(slot, pads)).filter(Boolean);
    return voters.includes(device) ? device : voters.length === 1 ? voters[0] : undefined;
  }

  function chooseArena(levelId: string, device = lastMenuDevice) {
    if (!matchIsOver() || !arenaVoteChoices().includes(levelId)) return;
    const voter = arenaVoter(device);
    if (!voter) return;
    finale.skip();
    if (online && room && partyView?.matchId) {
      if (room.role === 'host') {
        party?.setArenaVote(room.id, levelId, partyView.matchId);
        broadcastParty();
      } else room.send('host', { t: 'arena-vote', epoch: partyView.epoch, matchId: partyView.matchId, levelId });
    } else {
      localArenaVotes.set(voter, levelId);
      localRematchVotes.delete(voter);
    }
  }

  function cycleArenaVote(delta: number, device: string) {
    const choices = arenaVoteChoices(), voter = arenaVoter(device);
    if (!voter || !choices.length) return;
    const current = online ? partyView?.arenaVotes?.find((vote) => vote.peer === room?.id)?.levelId : localArenaVotes.get(voter);
    const index = choices.indexOf(current ?? choices[0]!);
    chooseArena(choices[(index + delta + choices.length) % choices.length]!, device);
  }

  function drawArenaVote(visible: boolean, readyText: string) {
    const choices = visible ? arenaVoteChoices() : [];
    hud.arenaVote.classList.toggle('hidden', !choices.length);
    hudEl.classList.toggle('arena-ballot-open', !!choices.length);
    // Readiness belongs beside the arena ballot while the ballot is open.
    hud.rematchVotes.classList.toggle('hidden', !!choices.length);
    if (!choices.length) {
      if (hud.highlights.parentElement !== hudEl) hudEl.append(hud.highlights);
      arenaVoteRenderKey = ''; return;
    }
    const voter = arenaVoter(lastMenuDevice);
    const votes = online ? (partyView?.arenaVotes ?? []).map((vote) => vote.levelId) : [...localArenaVotes.values()];
    const selected = online ? partyView?.arenaVotes?.find((vote) => vote.peer === room?.id)?.levelId : voter ? localArenaVotes.get(voter) : undefined;
    const confirm = inputPresentation.device.kind === 'gamepad' ? buttonLabel(0, inputPresentation.device, settings.controllerGlyphs) : 'Enter';
    const directions = inputPresentation.device.kind === 'gamepad' ? 'D-pad / left stick' : '← →';
    const key = JSON.stringify([choices, votes, selected, readyText, confirm]);
    if (key === arenaVoteRenderKey) return;
    arenaVoteRenderKey = key;
    const focused = (document.activeElement as HTMLElement | null)?.dataset.arenaVote;
    hud.arenaVote.replaceChildren();
    const heading = document.createElement('strong'); heading.textContent = 'Next arena';
    const row = document.createElement('div'); row.className = 'arena-ballots';
    const pool = [...builtInMatchLevels(), ...extraLevels];
    for (const id of choices) {
      const level = pool.find((level) => level.id === id);
      if (!level) continue;
      const button = document.createElement('button'); button.type = 'button'; button.className = 'arena-ballot';
      button.dataset.arenaVote = id; button.setAttribute('aria-pressed', String(id === selected));
      button.innerHTML = arenaPreviewSvg(level);
      const name = document.createElement('span'); name.textContent = level.name;
      const total = document.createElement('span'); const count = votes.filter((vote) => vote === id).length; total.textContent = `${count} ${count === 1 ? 'vote' : 'votes'}`;
      button.append(name, total); button.setAttribute('aria-label', `Vote for ${level.name}`);
      button.addEventListener('click', () => chooseArena(id)); row.append(button);
    }
    const hint = document.createElement('p'); hint.className = 'arena-vote-hint'; hint.textContent = `${readyText ? readyText + ' · ' : ''}${directions} choose · ${confirm} confirm · Ties use the first tied card.`;
    hud.arenaVote.append(hud.highlights, heading, row, hint);
    if (focused) [...row.querySelectorAll<HTMLButtonElement>('button')].find((button) => button.dataset.arenaVote === focused)?.focus({ preventScroll: true });
  }

  function voteRematch() {
    if (!room || !partyView || partyView.phase !== 'rematch' || !partyView.matchId) return;
    const vote = !partyView.votes.includes(room.id);
    if (room.role === 'host') {
      if (!sim || sim.ecs.get(RoundState)?.phase !== RoundPhase.MatchOver) return;
      party?.setVote(room.id, vote, partyView.matchId);
      broadcastParty();
      if (party?.allVoted) void startOnlineMatch();
    } else room.send('host', { t: 'rematch', epoch: partyView.epoch, matchId: partyView.matchId, vote });
  }

  async function joinOnline(role: 'host' | 'client') {
    if (room) return;
    const lobby = menus.lobby;
    lobby.name = lobby.name || `Player ${Math.floor(100 + Math.random() * 900)}`;
    localStorage.setItem(NAME_KEY, lobby.name);
    let code = normalizeRoomCode(menus.roomCode);
    if (role === 'host' && !code) code = randomRoomCode();
    if (!isRoomCode(code)) {
      menus.notice = role === 'host' ? 'Room codes are 4–8 letters and digits.' : 'Enter the room code your host shared (4–8 letters and digits).';
      show();
      return;
    }
    menus.roomCode = code;
    lobby.phase = 'connecting';
    lobby.role = role;
    menus.notice = '';
    show();
    try {
      const session = await connectRoom({ code, role, name: lobby.name });
      if (lobby.phase !== 'connecting') {
        // Cancelled while the socket was opening.
        session.close();
        return;
      }
      room = session;
      partyView = null;
      party = role === 'host' ? createParty(session.hostId, session.peers.keys()) : null;
      ownPartyCharacter = characterStyle(settings.characters[menus.seats.find((seat) => seat.taken)?.color ?? 0]);
      profiles = role === 'host' ? createPartyProfiles(session.hostId, ownPartyCharacter) : null;
      profileSent = false;
      wireRoom(session);
      lobby.phase = 'connected';
      lobby.shareUrl = shareUrl(code);
      lobby.maxHp = settings.maxHp;
      lobby.firstTo = settings.firstTo;
      lobby.ping = 0;
      menus.chat = [];
      history.replaceState(null, '', shareUrl(code));
      pushChat(role === 'host' ? `* you opened room ${code} — share the link or the code` : `* you joined room ${code}`);
      refreshLobbyPeers();
      if (role === 'host') broadcastLobby();
      pingTimer = window.setInterval(() => {
        if (!room) return;
        lobby.ping = room.rtt;
        const el = menusEl.querySelector('#ping');
        if (el) el.textContent = String(lobby.ping || '–');
      }, 2000);
    } catch (err) {
      lobby.phase = 'idle';
      menus.notice = err instanceof RoomError ? err.message : 'Could not connect to the room.';
      telemetry.error({ kind: 'room', message: err instanceof RoomError ? err.code : 'unknown', source: role, context: 'menu', renderer: rendererKind });
    }
    show();
  }

  function wireRoom(session: RoomSession) {
    session.on('peer', (peer) => {
      pushChat(`* ${peer.name} joined`);
      if (session.role === 'host') broadcastLobby();
      else refreshLobbyPeers();
      if (menus.screen === 'lobby') show();
    });
    session.on('left', (id) => {
      const slot = online?.slots.findIndex((s) => s.peer === id) ?? -1;
      const name = (slot >= 0 ? online?.names[slot] : undefined) ?? menus.lobby.peers.find((p) => p.id === id)?.name ?? 'someone';
      pushChat(`* ${name} left`);
      if (online?.role === 'host' && slot >= 0) online.remote.clear(slot);
      if (session.role === 'host') broadcastLobby();
      else refreshLobbyPeers();
      if (menus.screen === 'lobby') show();
    });
    session.on('closed', (reason) => {
      teardownRoom('disconnected');
      menus.notice = reason === 'host-left' ? 'The host left the room.' : 'Connection to the room was lost.';
      menus.screen = 'lobby';
      show();
    });
    session.on('message', onNetMessage);
  }

  /** `outcome` is how a match still running ends up in the statistics. */
  function teardownRoom(outcome: MatchOutcome = 'left') {
    mixer.stopMusic();
    window.clearInterval(pingTimer);
    room?.close();
    room = null;
    party = null;
    partyView = null;
    profiles = null;
    profileSent = false;
    onlineCharacters = [];
    online = null;
    menus.online = false;
    trackMatchEnd(outcome);
    sim?.destroy();
    sim = null;
    paused = false;
    slotDevices = [];
    resetBanner();
    menus.lobby.phase = 'idle';
    menus.lobby.peers = [];
    menus.lobby.inMatch = false;
    menus.lobby.party = null;
    menus.lobby.rules = undefined;
    menus.lobby.presetId = undefined;
    menus.chat = [];
    history.replaceState(null, '', location.pathname);
  }

  function leaveRoom() {
    teardownRoom();
    menus.screen = 'lobby';
    show();
  }

  /** Host: deal a match to everyone in the room (also the rematch). */
  let lastRoundRecoveryAt = -Infinity;
  let roundRecoveryTimes: number[] = [];
  async function startOnlineMatch() {
    if (!room || room.role !== 'host' || matchStarting) return;
    const session = room;
    if (sim && online && sim.ecs.get(RoundState)?.phase !== RoundPhase.MatchOver && menus.screen === 'lobby') {
      // "Back to match": the host only peeked at the lobby.
      menus.screen = 'play';
      show();
      return;
    }
    if (!party?.canStart) {
      menus.notice = 'Everyone must be ready before the match starts.';
      show();
      return;
    }
    const partyEpoch = party.snapshot().epoch;
    matchStarting = true;
    try {
      const library = settings.includeUserLevels ? await loadLibrary().catch(() => []) : [];
      if (room !== session || !session.open) return;
      extraLevels = library.flatMap((raw) => {
        try { return [parseLevel(raw)]; } catch { return []; }
      });
      const slots = roomSlots();
      const bots = Math.min(menus.bots, MAX_PEERS - slots.length);
      for (let i = 0; i < bots; i++) slots.push({ peer: null, name: `Bot ${slots.length + 1}`, color: slots.length, character: characterStyle(undefined, slots.length) });
      const rules = nextOnlineRules();
      rules.enabledLevels = resolvePresetLevels(rules, slots.length);
      const pool = matchLevelPool(rules.enabledLevels, extraLevels, slots.length, rules.mode);
      const ballot = party.snapshot();
      const votedArena = winningArena(ballot.arenaChoices ?? [], (ballot.arenaVotes ?? []).map((vote) => vote.levelId));
      const level = pool.find((level) => level.id === votedArena) ?? (rules.rotation === 'ordered' ? (pool[0] ?? gymLevel) : (pool[Math.floor(Math.random() * pool.length)] ?? gymLevel));
      const seed = (Math.random() * 1e9) | 0;
      const matchId = crypto.getRandomValues(new Uint32Array(4)).join('-');
      const start: NetMessage = { t: 'start', version: ROOM_PROTOCOL_VERSION, matchId, seed, levelId: level.id, slots, rules, extraLevels };
      if (!readRoomMessage(start, 'host')) {
        menus.notice = 'The selected rules or custom arena library cannot be sent online. Reduce the library and try again.';
        show();
        return;
      }
      if (!party.startMatch(matchId, partyEpoch)) return;
      if (!room.send('all', start)) { party.returnToLobby(); broadcastParty(); return; }
      menus.notice = '';
      lastRoundRecoveryAt = -Infinity;
      roundRecoveryTimes = [];
      beginOnlineMatch('host', seed, level, slots, rules, matchId);
      broadcastLobby();
    } finally {
      matchStarting = false;
    }
  }

  /** Everyone: build this browser's mirror of the match. */
  function beginOnlineMatch(role: 'host' | 'client', seed: number, level: LevelDef, slots: MatchSlot[], rules: MatchRules, matchId: string, recovery?: RoundRecovery) {
    if (!room) return;
    const me = room.id;
    online = createOnlineMatch(role, slots, me, (input) => room?.send('host', { t: 'input', matchId, input }), matchId);
    onlineCharacters = Array.from({ length: MAX_PLAYERS }, (_, color) => characterStyle(slots.find((slot) => slot.color === color)?.character, color));
    menus.online = true;
    menus.lobby.inMatch = true;
    const humans = slots.filter((s) => s.peer !== null).length;
    startSim(level, {
      playerCount: humans,
      bots: slots.length - humans,
      seed,
      maxHp: rules.maxHp,
      firstTo: rules.firstTo,
      devices: slots.map((s) => (s.peer === me ? 'auto' : '')),
      colors: slots.map((s) => s.color),
      rules,
      recovery: !!recovery,
    });
    if (sim) {
      if (recovery) {
        applyRoundProgress(sim.ecs, recovery);
        online.recovering = role === 'client';
        online.desync = role === 'client';
        online.recoveryNoticeUntil = performance.now() + 5000;
        pushChat(rules.mode === 'arms-race' ? '* Arena restarted to restore sync — weapon progress kept.' : '* Round restarted to restore sync — scores kept.');
        recordCompletedMatch();
      }
      online.outboxStart = sim.ctx.tick;
      online.nextTick = sim.ctx.tick;
    }
  }

  /** Rebuild every peer from the same fresh seed; partial physics snapshots cannot safely repair this sim. */
  function restartOnlineRound() {
    if (!room || room.role !== 'host' || !online || !sim) return;
    const now = performance.now();
    if (now - lastRoundRecoveryAt < 3000) return;
    roundRecoveryTimes = roundRecoveryTimes.filter((at) => now - at < 30_000);
    if (roundRecoveryTimes.length >= 3) {
      endOnlineMatch('sync could not be restored — refresh all players before starting again');
      return;
    }
    roundRecoveryTimes.push(now);
    lastRoundRecoveryAt = now;
    const recovery = captureRoundProgress(sim.ecs);
    const slots = online.slots;
    const level = sim.ctx.level;
    const s = sim.ctx.settings;
    const rules: MatchRules = { ...partyOptions(s), maxHp: s.maxHp, firstTo: s.firstTo, showWins: s.showWins, rotation: s.rotation,
      enabledWeapons: s.enabledWeapons, enabledLevels: s.enabledLevels, botDifficulty: s.botDifficulty, roundTimeLimit: s.roundTimeLimit };
    const seed = (Math.random() * 1e9) | 0;
    const matchId = crypto.getRandomValues(new Uint32Array(4)).join('-');
    const start: NetMessage = { t: 'start', version: ROOM_PROTOCOL_VERSION, matchId, seed, levelId: level.id, slots, rules, extraLevels, recovery };
    if (!readRoomMessage(start, 'host') || !room.send('all', start)) {
      endOnlineMatch('the round could not be restarted safely');
      return;
    }
    party?.recoverMatch(online.matchId, matchId);
    beginOnlineMatch('host', seed, level, slots, rules, matchId, recovery);
    broadcastLobby();
  }

  function returnToParty() {
    if (room) {
      if (room.role === 'host') endOnlineMatch('back to the party');
      else endOnlineLocal('', 'left');
    } else {
      for (const seat of menus.seats) seat.ready = false;
      quitToMenu();
    }
  }

  /** Host: stop the match for everyone; the room stays open. */
  function endOnlineMatch(reason: string) {
    if (room?.role === 'host') room.send('all', { t: 'end', reason });
    endOnlineLocal(reason, 'host-ended');
    party?.returnToLobby();
    if (room?.role === 'host') broadcastLobby();
  }

  /** Drop this browser's copy of the match and go back to the lobby. */
  function endOnlineLocal(reason: string, outcome: MatchOutcome) {
    trackMatchEnd(outcome);
    mixer.stopMusic();
    sim?.destroy();
    sim = null;
    online = null;
    menus.online = false;
    paused = false;
    hitStop = 0;
    slotDevices = [];
    resetBanner();
    if (reason) pushChat(`* match over — ${reason}`);
    menus.screen = 'lobby';
    show();
  }

  /** Client: stop the broken mirror and ask for a coordinated round restart, at most every three seconds. */
  function requestResync(why: string) {
    if (!online || !room || !sim) return;
    const now = performance.now();
    online.desync = true;
    online.recovering = false;
    online.inbox.length = 0;
    if (now - online.lastResyncAt < 3000) return;
    online.lastResyncAt = now;
    if (tracked) tracked.resyncs += 1;
    telemetry.error({ kind: 'desync', message: why, source: `tick ${sim.ctx.tick}`, context: 'online', renderer: rendererKind });
    console.warn(`[online] out of sync (${why}) at tick ${sim.ctx.tick}; asking the host to restart this round`);
    room.send('host', { t: 'resync', matchId: online.matchId, tick: sim.ctx.tick });
  }

  function onNetMessage(msg: NetMessage, from: number) {
    if (!room || !room.peers.has(from)) return;
    const isHost = room.role === 'host';
    switch (msg.t) {
      case 'profile': {
        if (isHost && profiles?.receive(from, msg)) broadcastLobby();
        return;
      }
      case 'lobby': {
        if (isHost) return;
        menus.lobby.maxHp = msg.maxHp;
        menus.lobby.firstTo = msg.firstTo;
        menus.lobby.bots = msg.bots;
        menus.lobby.inMatch = msg.inMatch;
        menus.lobby.partyRules = partyOptions(msg.partyRules);
        menus.lobby.rules = msg.rules;
        menus.lobby.presetId = msg.presetId;
        refreshLobbyPeers(msg.peers);
        if (!profileSent) profileSent = room.send('host', { t: 'profile', character: ownPartyCharacter });
        if (menus.screen === 'lobby') show();
        return;
      }
      case 'party': {
        if (isHost) return;
        const next = acceptPartySnapshot(partyView, msg, from, room.hostId);
        if (!next) return;
        partyView = next;
        menus.lobby.party = next;
        if (menus.screen === 'lobby') show();
        return;
      }
      case 'ready':
      case 'rematch':
      case 'arena-vote': {
        if (!isHost || !party?.receive(from, msg)) return;
        broadcastParty();
        if (party.allVoted && sim?.ecs.get(RoundState)?.phase === RoundPhase.MatchOver) void startOnlineMatch();
        return;
      }
      case 'start': {
        if (isHost || from !== room.hostId) return;
        if (msg.matchId === online?.matchId || !msg.slots.some((slot) => slot.peer === room!.id)) return;
        // The room parser validates the entire message and custom level schema before this handler.
        extraLevels = msg.extraLevels;
        const pool = matchLevelPool(msg.rules.enabledLevels, extraLevels, msg.slots.length, msg.rules.mode);
        const level = pool.find((l) => l.id === msg.levelId) ?? (msg.levelId === gymLevel.id ? gymLevel : undefined);
        if (!level) {
          menus.notice = `The host started on a level this build does not have (${msg.levelId}).`;
          show();
          return;
        }
        beginOnlineMatch('client', msg.seed, level, msg.slots, msg.rules, msg.matchId, msg.recovery);
        return;
      }
      case 'frames': {
        if (isHost || !online || !sim || from !== room.hostId || msg.matchId !== online.matchId) return;
        if (online.desync && !online.recovering) { requestResync('waiting for a round restart'); return; }
        if (!acceptFrames(online, msg.start, msg.frames, msg.hash)) requestResync('missed frames');
        return;
      }
      case 'input': {
        if (!isHost || !online || msg.matchId !== online.matchId) return;
        const slot = online.slots.findIndex((s) => s.peer === from);
        if (slot >= 0) online.remote.push(slot, msg.input);
        return;
      }
      case 'end':
        if (isHost || from !== room.hostId) return;
        menus.lobby.inMatch = false;
        if (online) endOnlineLocal(msg.reason, 'host-ended');
        else if (menus.screen === 'lobby') show();
        return;
      case 'chat': {
        const chat = isHost ? { ...msg, from: room.peers.get(from)?.name ?? 'Player' } : msg;
        pushChat(`${cleanPeerName(chat.from)}: ${chat.text}`);
        if (isHost) {
          for (const id of room.peers.keys()) if (id !== from && id !== room.id) room.send(id, chat);
        }
        if (menus.screen === 'lobby') show();
        return;
      }
      case 'resync':
        if (!isHost || !sim || !online || msg.matchId !== online.matchId || !online.slots.some((slot) => slot.peer === from)) return;
        restartOnlineRound();
        return;
      default:
        return;
    }
  }

  /** Screens whose controls are plain DOM the cursor can walk; the others have bespoke pad handling. */
  const NAV_SCREENS = new Set(['menu', 'solo', 'character', 'party', 'settings', 'lobby', 'pause', 'disconnect', 'scoreboard']);

  function backOut() {
    if (menus.screen === 'party') { menus.screen = menus.partyOrigin; show(); }
    else if (menus.screen === 'pause') setPaused(false);
    else if (menus.screen === 'settings') closeSettings();
    else if (menus.screen === 'lobby') {
      // Leaving a room is deliberate (the Leave button); Back only closes the empty form.
      if (menus.lobby.phase !== 'idle') return;
      menus.screen = 'menu';
      show();
    } else if (menus.screen === 'character') {
      menus.screen = menus.characterOrigin;
      show();
    } else if (menus.screen === 'join' || menus.screen === 'solo') {
      menus.screen = 'menu';
      show();
    }
  }

  /** Walk the focused menu with a direction / accept / back triple (shared by pads and arrow keys). */
  function navigateMenu(dir: NavDir | null, accept: boolean, back: boolean) {
    if (dir) {
      const cur = focusedIn(menusEl);
      if (!(cur && nudge(cur, dir))) moveFocus(menusEl, dir);
    }
    if (accept) {
      const cur = focusedIn(menusEl) ?? defaultFocus(menusEl);
      if (cur) {
        setFocus(menusEl, cur);
        activate(cur);
      }
    }
    if (back) backOut();
  }

  function dirOf(e: PadEdges): NavDir | null {
    return e.up ? 'up' : e.down ? 'down' : e.left ? 'left' : e.right ? 'right' : null;
  }

  /** One pad's menu presses this frame, applied to whatever screen is up. */
  function handlePadMenu(pad: Gamepad, e: PadEdges) {
    if (!(e.a || e.b || e.x || e.start || e.select || e.up || e.down || e.left || e.right)) return;
    applyPadMenu(pad, e);
    // Landing on a fresh screen: show the cursor straight away so the next press has a target.
    if (NAV_SCREENS.has(menus.screen) && !focusedIn(menusEl)) setFocus(menusEl, defaultFocus(menusEl));
  }

  function applyPadMenu(pad: Gamepad, e: PadEdges) {
    const device = padKey(pad);
    if (e.a || e.b || e.x || e.start || e.select) lastMenuDevice = device;
    const screen = menus.screen;
    if (screen === 'character' && menus.characterOrigin !== 'lobby' && menus.seats[menus.characterSeat]?.padId !== device) return;
    if (screen === 'play') {
      if (!sim) return;
      if (finale.active && e.a) { finale.skip(); return; }
      if (menus.editorPlaytest && e.select) { returnToEditor(); return; }
      if (practice?.step === 5 && e.start) { hud.practiceStart.click(); return; }
      if (matchIsOver()) {
        if (e.b || e.select) { leaveFinishedMatch(); return; }
        if (e.left || e.right) { lastMenuDevice = device; cycleArenaVote(e.right ? 1 : -1, device); }
        if (e.a || e.start) rematch();
      } else if (e.start) {
        setPaused(true);
      }
      return;
    }
    if (screen === 'editor') return;
    if (screen === 'solo' && e.start) { startQuickMatch(); return; }
    if (screen === 'join') {
      if (e.select) { openPartyRules(); return; }
      if (pad.mapping && pad.mapping !== 'standard' && !maps[pad.id]) {
        menus.notice = `Non-standard pad “${pad.id}” — open Settings to remap.`;
      }
      if (e.a) takeOrReadySeat(menus.seats, device, padLabel(pad));
      if (e.b) {
        backOut();
        return;
      }
      if (e.start) {
        startIfReady();
        return;
      }
      const seat = menus.seats.find((s) => s.taken && s.padId === device);
      if (e.x && seat) { openCharacter(menus.seats.indexOf(seat)); return; }
      if ((e.left || e.right) && seat) cycleSeatColor(seat, e.right ? 1 : -1, menus.seats);
      if (e.up || e.down) menus.bots = Math.max(0, Math.min(maxBots(menus.seats), menus.bots + (e.up ? 1 : -1)));
      show();
      return;
    }
    if (screen === 'pause' && (e.start || e.b)) {
      setPaused(false);
      return;
    }
    navigateMenu(dirOf(e), e.a || e.start, e.b);
  }

  function sampleInputs(): PlayerInput[] {
    const now = performance.now();
    const inputs = blankInputs(sim?.ctx.players.length ?? MAX_PLAYERS);
    const pads = pollGamepads();
    if (inputPresentation.update(pads)) {
      const device = inputPresentation.device;
      lastMenuDevice = device.kind === 'gamepad' ? padKey(device) : 'keyboard';
      syncControlHints();
    }
    const padInputs: (PlayerInput | undefined)[] = [];
    pads.forEach((pad, i) => {
      if (!pad) return;
      const latch = (latches[i] ??= emptyLatch());
      const aiming = menus.screen === 'play' && !paused;
      const aim = aiming ? lastAim[i] ?? { x: 0, y: 0 } : { x: 0, y: 0 };
      const map = padMapFor(pad.id);
      const input = readPad(pad, latch, aim, map, settings, now);
      // Start is edge-detected below (menus, pause, rematch); the latch would otherwise re-fire.
      latch.pause = false;
      padInputs[i] = input;
      if (aiming) lastAim[i] = aim;
      const tracker = (padEdges[i] ??= createPadEdgeTracker());
      handlePadMenu(pad, tracker.update(pad, now, (map ?? DEFAULT_MAP).pause));
    });

    // Each fighter slot reads the device that took its seat; the keyboard aims with the mouse
    // relative to its own fighter.
    for (let slot = 0; slot < inputs.length; slot++) {
      const device = deviceForSlot(slot, pads);
      if (!device) continue;
      // Online, this browser's fighter takes the first pad that has shown itself, else the keyboard.
      const autoPad = device === 'auto' ? pads.find(Boolean) : undefined;
      if (device === 'keyboard' || (device === 'auto' && !autoPad)) {
        const p = sim?.players().find((e) => e.get(Player)?.slot === slot);
        const t = p?.get(Transform) ?? { x: 8, y: 6 };
        let weapon;
        if (sim && p) {
          for (const held of sim.ecs.query(Weapon, Held)) {
            if (held.targetFor(HeldBy) === p) { weapon = weaponByIndex(held.get(Weapon)!.defId); break; }
          }
        }
        inputs[slot] = keys.sample({ x: t.x, y: t.y, ducking: p?.get(Controller)?.ducking, height: sim?.ctx.tuning.height, weapon }, cam, view.w, view.h);
      } else {
        const input = padInputs[autoPad ? autoPad.index : padIndexOf(device)];
        if (input) inputs[slot] = input;
      }
    }

    const pauseTap = keys.takePause();
    if (pauseTap && sim && !matchIsOver() && (menus.screen === 'play' || menus.screen === 'pause' || menus.screen === 'disconnect')) setPaused(menus.screen === 'play');
    return inputs;
  }

  function rumble(kind: RumbleKind, slot: number) {
    if (!settings.haptics || typeof navigator === 'undefined' || !navigator.getGamepads) return;
    const pads = pollGamepads();
    const device = deviceForSlot(slot, pads);
    const padIndex = device === 'auto' ? pads.find(Boolean)?.index : isPadKey(device) ? padIndexOf(device) : undefined;
    for (const pad of pads) {
      if (!pad || pad.index !== padIndex) continue;
      const actuator = pad?.vibrationActuator;
      if (!actuator?.playEffect) continue;
      void actuator.playEffect('dual-rumble', {
        duration: kind === 'boom' ? 120 : kind === 'block' ? 40 : 60,
        strongMagnitude: kind === 'boom' ? 0.65 : kind === 'block' ? 0.12 : 0.35,
        weakMagnitude: kind === 'block' ? 0.2 : 0.4,
      });
    }
  }

  let lastFrameError = '';
  function tick(now: number) {
    if (stopped) return;
    try {
      frame(now);
    } catch (err) {
      // One bad frame must not take the whole game down: keep the loop alive and shout once per fault.
      const msg = err instanceof Error ? err.message : String(err);
      if (msg !== lastFrameError) {
        lastFrameError = msg;
        console.error('frame error', err);
        telemetry.error({
          kind: 'frame',
          message: msg,
          source: err instanceof Error ? (err.stack?.split('\n')[1]?.trim() ?? '') : '',
          context: tracked ? tracked.mode : 'menu',
          renderer: rendererKind,
        });
      }
    }
    raf = requestAnimationFrame(tick);
  }

  /** One sim tick with the given inputs plus everything the presentation hangs off its events. */
  function simTick(sampled: PlayerInput[]) {
    if (!sim) return;
    recorder.push(sampled);
    const events = sim.step(sampled);
    observeVisualEvents(sim.ecs, events, sim.ctx.tick);
    if (practice) {
      if (recoverPractice(practice, sim)) {
        if (practice.step >= 2 && practice.step <= 4) debugApi.giveWeapon(0, 'pistol');
        keys.clear();
        latches.forEach(consumeLatch);
        showFeedback('Back on your feet — keep practicing', 'practice');
        updatePracticeCoach();
        return;
      }
      const player = sim.players()[0]!;
      const pos = player.get(Transform)!;
      const ctrl = player.get(Controller);
      if (advancePractice(practice, player, pos.x, ctrl?.grounded ?? true, ctrl?.vy ?? 0, sim.ctx.inputs[0] ?? EMPTY_INPUT, events)) {
        if (practice.step === 2 || practice.step === 4) debugApi.giveWeapon(0, 'pistol');
        updatePracticeCoach();
      }
    }
    const localSlot = online?.localSlot ?? 0;
    const localPlayers = sim.players().filter((p) => online ? p.get(Player)!.slot === online.localSlot : p.get(Player)!.slot < humanCount);
    const isLocal = (entity: number) => localPlayers.some((p) => p === entity);
    if (events.some((e) => e.type === 'round-phase' && e.phase === 'countdown')) {
      hud.feed.clear();
      hud.toastUntil = 0;
    }
    finale.observe(events, (entity) => sim?.players().find((p) => p === entity)?.get(Player)?.slot);
    for (const event of events) {
      if (event.type === 'kill-streak') {
        const player = sim.players().find((p) => p === event.player)?.get(Player);
        if (player) {
          const palette = settings.colorblind ? PLAYER_COLORS_CB : PLAYER_COLORS;
          hud.feed.milestone(slotName(player.slot), event.streak, palette[player.color % palette.length]!, performance.now());
        }
      } else if (event.type === 'siphon' && isLocal(event.source)) {
        showFeedback(`+${Math.round(event.amount * 10) / 10} HP`, 'heal', 'siphon');
      } else if (event.type === 'pickup' && sim.players().find((p) => p === event.player)?.get(Player)?.slot === localSlot) {
        const tip = event.weaponId === 'siphon' ? ' · Damage restores HP' : event.weaponId === 'return-disc' ? ' · Attack to launch; it returns to you' : '';
        showFeedback(`Picked up ${weaponDisplayName(WEAPON_BY_ID.get(event.weaponId)!.def)}${tip}`, 'pickup', event.weaponId);
      } else if (event.type === 'block' && event.reflected && sim.players().find((p) => p === event.player)?.get(Player)?.slot === localSlot) {
        showFeedback('Perfect block!', 'block');
      } else if (event.type === 'kill') {
        const target = sim.players().find((p) => p === event.target)?.get(Player)?.slot;
        const source = sim.players().find((p) => p === event.source)?.get(Player)?.slot;
        if (target !== undefined) {
          const palette = settings.colorblind ? PLAYER_COLORS_CB : PLAYER_COLORS;
          const color = sim.players().find((p) => p === event.target)?.get(Player)?.color ?? target;
          hud.feed.add(event, slotName(target), source === undefined ? undefined : slotName(source), palette[color % palette.length]!, isLocal(event.target) || isLocal(event.source), performance.now());
          if (isLocal(event.target)) {
            const who = localPlayers.length === 1 ? 'You' : slotName(target);
            showFeedback(`${who} ${who === 'You' ? 'were' : 'was'} knocked out${source !== undefined && source !== target ? ` by ${slotName(source)}` : ''} · ${knockoutDetail(event)}`, 'death');
          }
        }
      }
    }
    if (sim.ctx.level !== renderedLevel) {
      // The sim rotated to the next arena: last round's blood and embers must not carry over.
      renderedLevel = sim.ctx.level;
      rotatedThisFrame = true;
      particles.clear();
      decals.length = 0;
      decalPool.release(decalLayer);
      decalLayer = decalPool.acquire(renderedLevel.bounds);
    }
    mixer.handle(events);
    if (events.some((e) => e.type === 'round-phase' && e.phase === 'match-over')) mixer.stopMusic();
    emitFromEvents(events, particles, decals, { bloodScale: settings.reduceBlood ? 0 : sim.ctx.settings.playerCount + sim.ctx.settings.bots > 4 ? 0.4 : 0.65 });
    if (stressTarget > 0) {
      if (sim.ctx.tick % 30 === 0) armFighters(sim, stressRng);
      if (particles.count < stressTarget) emitFromEvents(stressEvents(sim, stressRng), particles, decals, { bloodScale: settings.reduceBlood ? 0 : 0.4 });
    }
    const world = sim.ecs;
    decalLayer.stampNew(decals, (d) => (!settings.reduceBlood || d.kind === 'scorch') && snapDecalToSurface(world, d));
    // Once stamped into the texture the decal records are dead weight; keeping them for the whole
    // round only grew the old generation (a long stress round piles up tens of thousands).
    decals.length = 0;
    decalLayer.consumed = 0;
    const listeners = localPlayers.map((p) => ({
      entity: p,
      slot: p.get(Player)!.slot,
      ...p.get(Transform)!,
      // A fighter eliminated this tick should still feel the blast that eliminated them.
      alive: !p.has(Dead) || events.some((e) => e.type === 'kill' && e.target === p),
    }));
    const impact = combatImpact(events, listeners,
      localPlayers.length === 0 || localPlayers.every((p) => p.has(Dead)));
    hitStop = Math.max(hitStop, impact.stop);
    if (events.some((e) => e.type === 'kill')) {
      const kills = events.filter((e) => e.type === 'kill').length;
      recordKos(kills);
      if (tracked) tracked.kills += kills;
    }
    if (events.some((e) => e.type === 'round-phase' && e.phase === 'match-over')) {
      if (online?.role === 'host' && party?.finishMatch(online.matchId, rematchArenaChoices())) broadcastParty();
      recordCompletedMatch();
    }
    if (!settings.reduceShake && impact.shake) addShake(cam, impact.shake);
    for (const [slot, kind] of impact.rumbles) rumble(kind, slot);
    latches.forEach(consumeLatch);
    keys.consume();
  }

  function frame(now: number) {
    const dt = Math.min(0.05, (now - last) / 1000);
    let t = profiler.begin();
    profiler.sample('interval', now - last);
    // A 120 Hz frame is 8.3 ms; anything past 12 ms between rAF callbacks means a missed one. The
    // frame before it is what ran long, so that is the one logged.
    const heapNow = usedHeapMb();
    if (tracked && sim && menus.screen === 'play') {
      // Frame pacing while a match is actually being played: not the pause card, not the menus.
      perfAgg.frame(now - last);
      if (now - tracked.lastPerfAt >= PERF_EVERY_MS) flushPerf('periodic');
    }
    if (sim && menus.screen === 'play' && now - last > 12) {
      longFrames += 1;
      if (longFrameLog.length < 64) {
        // A heap that shrank across the gap points at a major GC rather than at our own frame work.
        longFrameLog.push({ at: Math.round(now), interval: +(now - last).toFixed(1), tick: sim.ctx.tick, before: profiler.last(), heapBefore: lastHeap, heapAfter: heapNow });
      }
    }
    lastHeap = heapNow;
    last = now;
    // Without a ResizeObserver the size has to be read each frame (a layout read). A DPR change
    // (window dragged to another display) keeps the CSS size, so it is checked here.
    if (!sizeObserver) measureCanvas();
    if (window.devicePixelRatio !== lastDpr) {
      lastDpr = window.devicePixelRatio;
      viewDirty = true;
    }
    if (renderer && viewDirty) {
      renderer.resize(view.w, view.h);
      viewDirty = false;
    }
    // Pads and keys are read every frame: they drive the join screen and pause toggling, not just the fight.
    const live = sampleInputs();
    t = profiler.lap('input', t);
    // The match stays on screen (still) behind the pause and disconnect cards.
    const showMatch = !!sim && (menus.screen === 'play' || menus.screen === 'pause' || menus.screen === 'disconnect' || (menus.screen === 'settings' && menus.settingsOrigin === 'pause'));
    if (hudShown !== showMatch) {
      hudShown = showMatch;
      hudEl.classList.toggle('hidden', !showMatch);
    }
    if (sim && showMatch) {
      // An online match never pauses for one player: the pause card is only a menu over the fight.
      const running = online ? true : menus.screen === 'play' && !paused;
      const slowmo = sim.ecs.get(RoundState)?.phase === RoundPhase.LastKill ? tuning.lastKillSlowmo : 1;
      const isClient = online?.role === 'client';
      // A client's clock leans toward keeping a few frames in hand (src/net/online.ts).
      const scale = isClient && online ? slowmo * pacingScale(online.inbox.length) : slowmo;
      let steps = running ? loop.consume(dt, scale) : 0;
      if (frozen) {
        steps = frozenSteps;
        frozenSteps = 0;
      }
      if (!running) {
        // Paused: drop the presses so a jump latched behind the menu does not fire on resume.
        latches.forEach(consumeLatch);
        keys.consume();
      }
      if (isClient && online) {
        // My input goes to the host; the sim only advances through frames the host has stepped.
        if (online.localSlot >= 0) online.sender?.update(menus.screen === 'play' ? (live[online.localSlot] ?? EMPTY_INPUT) : EMPTY_INPUT, now);
        steps = online.desync && !online.recovering ? 0 : clientSteps(steps, online.inbox.length);
        if (online.inbox.length === 0) online.stalledSince ||= now;
        else online.stalledSince = 0;
      }
      let hostHash: { tick: number; h: string } | undefined;
      for (let i = 0; i < steps; i++) {
        if (hitStop > 0) {
          hitStop -= 1;
          continue;
        }
        let sampled: PlayerInput[];
        let packed: PackedInput[] | null = null;
        if (isClient && online) {
          const next = online.inbox.shift();
          if (!next) break;
          sampled = next;
        } else if (online) {
          // Host: remote fighters step with what they sent, local ones with what the wire can carry,
          // so the frame broadcast below reproduces this tick bit for bit on every client.
          const live2 = applyScriptedInputs(live).slice(0, online.slots.length);
          const me = room?.id;
          packed = live2.map((input, s) => {
            const slot = online!.slots[s];
            return slot && slot.peer !== null && slot.peer !== me ? online!.remote.take(s) : packInput(menus.screen === 'play' ? input : EMPTY_INPUT);
          });
          sampled = packed.map(unpackInput);
        } else {
          sampled = applyScriptedInputs(live);
        }
        simTick(sampled);
        if (online?.role === 'host' && packed) {
          online.outbox.push(packed);
          if (sim.ctx.tick % HASH_EVERY_TICKS === 0) hostHash = { tick: sim.ctx.tick, h: sim.hash() };
        } else if (isClient && online) {
          const ok = checkHash(online, sim.ctx.tick, () => sim!.hash());
          if (ok === false) requestResync('world hash differs');
          else if (ok === true && online.recovering) {
            online.recovering = false;
            online.desync = false;
          }
        }
      }
      if (online?.role === 'host' && online.outbox.length && room) {
        room.send('all', { t: 'frames', matchId: online.matchId, start: online.outboxStart, frames: online.outbox, hash: hostHash });
        online.outboxStart = sim.ctx.tick;
        online.outbox = [];
      }
      profiler.sample('steps', steps);
      t = profiler.lap('sim', t);
      stepParticles(particles, dt);
      profiler.sample('particles', particles.count);
      t = profiler.lap('fx', t);
      const frame = buildFrame(
        sim,
        cam,
        frozen ? 1 : interpolationAlpha(loop),
        view.w,
        view.h,
        particles,
        {
          debug: debugDraw,
          freezeCamera: freezeCam,
          colorblind: settings.colorblind,
          characterStyles: online ? onlineCharacters : settings.characters,
          localSlots: online ? [online.localSlot] : Array.from({ length: humanCount }, (_, slot) => slot),
          reducedMotion: settings.reduceShake,
          decalLayer,
          flash: settings.reduceShake ? 0 : hitStop,
          // Limbs swing in the sim's time: slowed with the last-kill replay, held while paused.
          dt: running && !frozen ? dt * slowmo : 0,
          cameraDt: dt,
        },
      );
      // Hashing walks every networked entity; it is only ever read from the F3 overlay (which hashes
      // when it refreshes) and lazily through window.__floppy.lastHash, so ordinary frames skip it.
      frame.hud.gpuMs = renderer?.lastGpuMs ?? 0;
      profiler.sample('groups', frame.groups.length);
      lastFrame = frame;
      const replayEnabled = settings.showReplays && !settings.reduceShake && !practice && !menus.editorPlaytest;
      finale.capture(frame, now, replayEnabled);
      if (frame.hud.phase === RoundPhase.MatchOver) finale.start(now, replayEnabled);
      const replayFrame = menus.screen === 'play' ? finale.frame(now) : null;
      t = profiler.lap('build', t);
      renderer?.render(replayFrame ?? frame);
      if (renderer && renderer.gpuPassMs >= 0) profiler.sample('gpu', renderer.gpuPassMs);
      t = profiler.lap('render', t);
      drawHud(frame);
      profiler.lap('hud', t);
      profiler.end();
      // Stage means for the perf summary; every 8th frame is plenty and keeps the per-frame cost nil.
      if (tracked && running && (perfAgg.frames & 7) === 0) perfAgg.stages(profiler.last());
      if (rotatedThisFrame) {
        rotatedThisFrame = false;
        if (rotationLog.length < 64) rotationLog.push({ tick: sim.ctx.tick, level: renderedLevel.id, stages: profiler.last() });
      }
    } else if (renderer && !sim && DEMO_SCREENS.has(menus.screen)) {
      stepDemo(dt);
    } else if (renderer && menus.screen !== 'editor') {
      renderer.render({
        groups: [],
        camera: { x: 16, y: 9, zoom: 28, ppm: 28, shakeX: 0, shakeY: 0 },
        theme: { top: '#1b2030', bottom: '#111318', solid: '#333' },
        hud: { slowmo: false, countdown: 0 },
      });
    }
  }

  function setMatchButtonLabel(button: HTMLButtonElement, label: string, shortcut = '') {
    if (button.getAttribute('aria-label') === label && button.dataset.shortcut === shortcut) return;
    button.setAttribute('aria-label', label);
    button.dataset.shortcut = shortcut;
    button.textContent = label;
    if (shortcut) {
      const hint = document.createElement('span');
      hint.className = 'match-button-hint';
      hint.setAttribute('aria-hidden', 'true');
      hint.textContent = shortcut;
      button.prepend(hint);
    }
  }

  // The HUD is a handful of persistent DOM nodes; we only touch them when the
  // underlying value changes so CSS transitions/animations actually play.
  const hud = (() => {
    const mk = (cls: string, parent: HTMLElement = hudEl) => {
      const el = document.createElement('div');
      el.className = cls;
      parent.append(el);
      return el;
    };
    const bars = mk('hud-bars');
    // The score strip: title / fighter cards / verdict. Between rounds the strip itself grows into
    // the scorecard (CSS keyed on data-card), so the tally you watched all round is the scoreboard.
    const top = mk('hud-top');
    const title = mk('hud-title', top);
    const players = mk('hud-players', top);
    const sub = mk('hud-sub', top);
    const count = mk('hud-count');
    const level = mk('hud-level');
    const net = mk('hud-net');
    const info = mk('hud-info');
    const toast = mk('hud-feedback', info);
    toast.setAttribute('role', 'status');
    const feedRoot = mk('combat-feed');
    feedRoot.setAttribute('role', 'log');
    feedRoot.setAttribute('aria-live', 'off');
    feedRoot.setAttribute('aria-label', 'Recent knockouts');
    const feed = createCombatFeed(feedRoot);
    const danger = mk('hud-danger', info);
    const mode = mk('hud-mode', info);
    const objective = mk('hud-objective', info);
    const highlights = mk('hud-highlights hidden');
    const rematchVotes = mk('hud-rematch');
    const arenaVote = mk('hud-arena-vote hidden');
    const replayLabel = mk('hud-replay-label hidden');
    replayLabel.textContent = 'FINAL KNOCKOUT · REPLAY';
    const controls = mk('match-controls hidden');
    const matchButton = (text: string, action: () => void) => {
      const button = document.createElement('button');
      button.className = 'menu-btn small';
      setMatchButtonLabel(button, text);
      button.addEventListener('click', action);
      controls.append(button);
      return button;
    };
    const edit = matchButton('Edit arena', returnToEditor);
    edit.dataset.editorReturn = '';
    edit.title = 'Return to your draft';
    const replay = matchButton('Play again', rematch);
    replay.classList.add('primary');
    const skipReplay = matchButton('Skip replay', () => finale.skip());
    const backToParty = matchButton('Back to party', returnToParty);
    const pause = matchButton('Pause', () => setPaused(true));
    pause.title = 'Esc · Pause';
    const practice = mk('practice-coach hidden');
    const practiceTitle = document.createElement('strong');
    const practiceBody = document.createElement('p');
    const practiceStart = document.createElement('button');
    practiceStart.className = 'menu-btn small primary hidden';
    practiceStart.textContent = 'Start Quick Match';
    practiceStart.addEventListener('click', () => {
      lastMenuDevice = slotDevices[0] ?? 'keyboard';
      quitToMenu();
      clearSeats(menus.seats);
      const seat = takeOrReadySeat(menus.seats, lastMenuDevice, deviceLabel(lastMenuDevice));
      if (seat) seat.ready = true;
      menus.screen = 'solo';
      menus.bots = 3;
      menus.firstTo = 5;
      show();
    });
    practice.append(practiceTitle, practiceBody, practiceStart);
    const dbg = document.createElement('pre');
    dbg.className = 'hidden';
    dbg.style.cssText =
      'position:absolute;right:12px;top:10px;margin:0;padding:8px;background:rgba(0,0,0,0.55);font:12px/1.4 monospace';
    hudEl.append(dbg);
    return {
      bars,
      top,
      title,
      players,
      sub,
      count,
      level,
      net,
      toast,
      toastUntil: 0,
      toastPriority: 0,
      feed,
      danger,
      mode,
      objective,
      highlights,
      rematchVotes,
      arenaVote,
      replayLabel,
      skipReplay,
      backToParty,
      controls,
      edit,
      replay,
      pause,
      practice,
      practiceTitle,
      practiceBody,
      practiceStart,
      netText: '',
      dbg,
      lastCount: -1,
      /** Which fighters (slot/colour) the cards were built for; cards persist so their CSS transitions play. */
      lastRoster: '',
      cards: new Map<
        number,
        {
          card: HTMLElement;
          face: HTMLElement;
          tally: HTMLElement;
          wins: HTMLElement;
          hp: HTMLElement;
          weapon: HTMLElement;
          streak: HTMLElement;
          raceProgress: HTMLElement;
          raceStage: number;
          crown: HTMLElement | null;
          /** Last values written to the DOM, so unchanged frames write nothing. */
          alive: string;
          winner: string;
          hpPct: number;
        }
      >(),
      lastCardKey: '',
      lastWins: Array<number>(MAX_PLAYERS).fill(0),
      goUntil: 0,
      levelUntil: 0,
      lastLevel: '',
      slowmo: false,
      levelShown: false,
      dbgShown: false,
      /** When the F3 readout text was last rebuilt (it refreshes at 10 Hz, not per frame). */
      dbgAt: 0,
    };
  })();

  /** A finished match's scorecard must not linger or flash into the next match. */
  function resetBanner() {
    hud.feed.clear();
    hud.toast.textContent = '';
    hud.toastUntil = 0;
    hud.toast.classList.remove('show');
    hud.practice.classList.add('hidden');
    hud.controls.classList.add('hidden');
    hud.top.dataset.card = '0';
    delete hud.top.dataset.roundOver;
    hud.title.innerHTML = '';
    hud.sub.textContent = '';
    hud.lastCardKey = '';
    hud.lastRoster = '';
    hud.lastWins.fill(0);
    hud.players.innerHTML = '';
    hud.cards.clear();
  }

  function showFeedback(text: string, kind: string, weaponId?: string) {
    // Deaths and ladder promotions can replace each other; routine pickups cannot hide either.
    const priority = kind === 'weapon' || kind === 'death' ? 4 : kind === 'block' ? 3 : kind === 'heal' ? 2 : 1;
    if (performance.now() < hud.toastUntil && priority < hud.toastPriority) return;
    hud.toastPriority = priority;
    const def = weaponId ? WEAPON_BY_ID.get(weaponId)?.def : undefined;
    const label = document.createElement('span');
    label.textContent = text;
    hud.toast.replaceChildren(...(def ? [weaponIcon(def), label] : [label]));
    hud.toast.dataset.kind = kind;
    hud.toastUntil = performance.now() + (kind === 'death' ? 4000 : 2200);
    hud.toast.classList.add('show');
  }

  function updateModeHud(frame: RenderFrame) {
    if (!sim || practice || menus.editorPlaytest) {
      hud.mode.textContent = hud.objective.textContent = '';
      return;
    }
    const rules = sim.ctx.settings, status = frame.hud.party;
    const mode = rules.mode !== 'classic' || rules.mutators.length ? partySummary(partyOptions(rules)) : '';
    let objective = '';
    if (status?.armsRace) {
      const local = status.armsRace.players.find((p) => p.slot === (online?.localSlot ?? 0));
      if (local) {
        const displayWeapon = (id: string) => { const def = WEAPON_BY_ID.get(id)?.def; return def ? weaponDisplayName(def) : id; };
        objective = local.nextWeaponId
          ? `${displayWeapon(local.weaponId)} · ${local.stageKills}/${local.killsRequired} kills · Next: ${displayWeapon(local.nextWeaponId)}`
          : `${displayWeapon(local.weaponId)} · Final blade — one kill to win!`;
        if (local.respawnTicks > 0) objective += `\nBack in ${(local.respawnTicks / 60).toFixed(1)}s · Progress kept`;
      }
    } else if (status?.hill) {
      const control = status.hill.contested ? 'Contested — no points' : status.hill.owner >= 0 ? `${slotName(status.hill.owner)} controls the hill` : 'Stand inside the hill to score';
      objective = `${control}\n${(frame.hud.players ?? []).map((p) => `${slotName(p.slot)} ${Math.floor(status.teamScores[p.slot] ?? 0)}/${rules.modeScoreLimit}`).join(' · ')}`;
    } else if (status?.mode === 'shared-stock') {
      const device = inputPresentation.device;
      const block = gameplayHelp('block', device, settings.controllerGlyphs, device.kind === 'gamepad' ? maps[device.id] : undefined);
      objective = `Respawns · Sun ${status.stocks[0]} · Moon ${status.stocks[1]}\nOut of lives? Hold ${block} by a fallen teammate to rescue them.`;
      const respawning = status.downed.filter((p) => p.respawnTicks > 0);
      if (respawning.length) objective += `\n${respawning.map((p) => `${slotName(p.slot)} returns in ${Math.ceil(p.respawnTicks / 60)}`).join(' · ')}`;
    } else if (status?.mode === 'teams') {
      objective = 'Sun vs Moon · Last team standing wins';
    } else if (status?.mode === 'office-brawl') {
      objective = 'Office equipment only · Last fighter standing wins';
    }
    if (status?.roulette) {
      const weapon = WEAPON_BY_ID.get(status.roulette.weaponId)?.def;
      const loadout = weapon ? weaponDisplayName(weapon) : status.roulette.weaponId;
      objective += `${objective ? '\n' : ''}${loadout}${rules.mode === 'weapon-roulette' ? ` · New weapon in ${Math.ceil(status.roulette.ticksLeft / 60)}s` : ' · Shared loadout'}`;
    }
    if (frame.hud.phase === RoundPhase.MatchOver) objective = '';
    if (hud.mode.textContent !== mode) hud.mode.textContent = mode;
    if (hud.objective.textContent !== objective) hud.objective.textContent = objective;
  }

  function drawHud(frame: ReturnType<typeof buildFrame>) {
    const h = frame.hud;
    const final = h.phase === RoundPhase.MatchOver;
    const device = inputPresentation.device;
    const confirmShortcut = device.kind === 'gamepad' ? buttonLabel(0, device, settings.controllerGlyphs) : 'Enter';
    const backShortcut = device.kind === 'gamepad' ? buttonLabel(1, device, settings.controllerGlyphs) : 'Esc';
    hud.controls.classList.toggle('hidden', menus.screen !== 'play');
    hud.edit.classList.toggle('hidden', !menus.editorPlaytest);
    hud.replay.classList.toggle('hidden', !final || finale.active);
    hud.backToParty.classList.toggle('hidden', !final || menus.editorPlaytest);
    hud.pause.classList.toggle('hidden', final);
    hud.skipReplay.classList.toggle('hidden', !finale.active);
    hud.replayLabel.classList.toggle('hidden', !finale.active);
    hud.top.classList.toggle('replay-hidden', finale.active);
    const multiplayerVote = !!online || humanCount > 1;
    const voted = online ? !!room && !!partyView?.votes.includes(room.id) : localRematchVotes.has(lastMenuDevice);
    const replayText = multiplayerVote ? voted ? 'Cancel vote' : 'Vote rematch' : 'Play again';
    setMatchButtonLabel(hud.replay, replayText, confirmShortcut);
    setMatchButtonLabel(hud.backToParty, 'Back to party', backShortcut);
    setMatchButtonLabel(hud.skipReplay, 'Skip replay', confirmShortcut);
    setMatchButtonLabel(hud.edit, 'Edit arena', final ? backShortcut : device.kind === 'gamepad' ? buttonLabel(8, device, settings.controllerGlyphs) : '');
    hud.replay.disabled = !!online && partyView?.phase !== 'rematch';
    const pauseLabel = online ? 'Match menu' : 'Pause';
    setMatchButtonLabel(hud.pause, pauseLabel);
    const now = performance.now();
    const highlightText = final && !finale.active ? finale.highlights(slotName).join('\n') : '';
    if (hud.highlights.textContent !== highlightText) hud.highlights.textContent = highlightText;
    hud.highlights.classList.toggle('hidden', !highlightText);
    const voteText = final && multiplayerVote ? online
      ? `Rematch votes: ${partyView?.votes.length ?? 0} / ${partyView?.members.length ?? 0}`
      : `Rematch votes: ${localRematchVotes.size} / ${humanCount} · Each player presses ${device.kind === 'gamepad' ? confirmShortcut : 'Enter or their south face button'}` : '';
    if (hud.rematchVotes.textContent !== voteText) hud.rematchVotes.textContent = voteText;
    drawArenaVote(final && menus.screen === 'play' && !finale.active && !menus.editorPlaytest, voteText);
    updateModeHud(frame);
    mixer.setTension(h.danger?.active ? 1 : h.phase === RoundPhase.Fighting ? 0.45 : 0);

    if (now >= hud.toastUntil) hud.toast.classList.remove('show');
    hud.feed.expire(now);
    const dangerText = h.danger?.active ? 'SUDDEN DEATH · STAY INSIDE' : h.danger && h.danger.secondsLeft <= 10 ? `Sudden death in ${h.danger.secondsLeft}` : '';
    if (hud.danger.textContent !== dangerText) hud.danger.textContent = dangerText;
    hud.danger.classList.toggle('show', !!dangerText);

    if (hud.slowmo !== h.slowmo) {
      hud.slowmo = h.slowmo;
      hud.bars.classList.toggle('on', h.slowmo);
    }

    // Countdown numbers + GO!
    if (h.countdown !== hud.lastCount) {
      hud.count.innerHTML = '';
      if (h.countdown > 0) {
        const s = document.createElement('span');
        s.textContent = String(h.countdown);
        hud.count.append(s);
        hud.count.dataset.countdown = String(h.countdown);
      } else {
        delete hud.count.dataset.countdown;
        if (hud.lastCount > 0) {
          const s = document.createElement('span');
          s.className = 'go';
          s.textContent = 'FIGHT';
          hud.count.append(s);
          hud.goUntil = now + 700;
        }
      }
      hud.lastCount = h.countdown;
    } else if (hud.goUntil && now > hud.goUntil) {
      hud.count.innerHTML = '';
      hud.goUntil = 0;
    }

    // Level name: fades in during the countdown, out once the fight starts.
    if (h.levelName && h.levelName !== hud.lastLevel) {
      hud.level.textContent = h.levelName;
      hud.lastLevel = h.levelName;
    }
    const showLevel = h.countdown > 0 || h.phase === RoundPhase.Scoreboard;
    if (hud.levelShown !== showLevel) {
      hud.levelShown = showLevel;
      hud.level.classList.toggle('show', showLevel);
    }

    // Fighter cards. Built once per roster and then updated in place, so the strip's morph into the
    // scorecard (and back) is one continuous CSS transition rather than a rebuild.
    const plist = h.players ?? [];
    const wins = h.wins ?? [];
    const firstTo = h.firstTo ?? 0;
    const race = h.party?.armsRace;
    const over = h.phase === RoundPhase.LastKill || h.phase === RoundPhase.Scoreboard || h.phase === RoundPhase.MatchOver;
    const winnerSlot = !over ? -1 : h.phase === RoundPhase.MatchOver ? (h.matchWinner ?? -1) : (h.roundWinner ?? -1);
    const roster = plist.map((p) => `${p.slot}:${p.color}`).join('|') + (h.showWins ? 'w' : '') + (race ? 'r' : '');
    if (roster !== hud.lastRoster) {
      hud.lastRoster = roster;
      hud.top.dataset.largeRoster = plist.length > 4 ? '1' : '0';
      hud.players.innerHTML = '';
      hud.cards.clear();
      for (const p of plist) {
        const card = document.createElement('div');
        card.className = 'hud-p';
        card.style.setProperty('--c', p.color);
        card.dataset.slot = String(p.slot);
        card.dataset.local = String(online ? p.slot === online.localSlot : p.slot < humanCount);
        const face = document.createElement('div');
        face.className = 'hud-face';
        card.append(face);
        const tally = document.createElement('div');
        tally.className = 'hud-tally';
        const winsEl = document.createElement('div');
        winsEl.className = 'hud-wins';
        if (h.showWins && !race) card.append(tally, winsEl);
        const hp = document.createElement('div');
        hp.className = 'hud-hp';
        hp.append(document.createElement('i'));
        card.append(hp);
        const name = document.createElement('div');
        name.className = 'hud-name';
        name.textContent = `${slotName(p.slot)}${p.team !== undefined ? ` · ${p.team === 0 ? 'Sun' : 'Moon'}` : ''}`.toUpperCase();
        card.append(name);
        const weapon = document.createElement('div');
        weapon.className = 'hud-weapon';
        card.append(weapon);
        const streak = document.createElement('div');
        streak.className = 'hud-streak';
        streak.hidden = true;
        card.append(streak);
        const raceProgress = document.createElement('div');
        raceProgress.className = 'hud-race-progress';
        if (race) card.append(raceProgress);
        hud.players.append(card);
        hud.cards.set(p.slot, { card, face, tally, wins: winsEl, hp: hp.firstElementChild as HTMLElement, weapon, streak, raceProgress, raceStage: -1, crown: null, alive: '', winner: '', hpPct: -1 });
      }
    }
    // Every write below is guarded by the value it last wrote: the DOM is only touched when a value
    // changes, so a steady frame costs no style or layout work at all.
    for (const p of plist) {
      const c = hud.cards.get(p.slot);
      if (!c) continue;
      const streakCount = (final ? p.bestStreak : p.streak) ?? 0;
      const streakText = `${streakCount} ${final ? 'BEST STREAK' : 'KO STREAK'}`;
      if (c.streak.textContent !== streakText) {
        c.streak.textContent = streakText;
        c.streak.title = final ? 'Best consecutive knockouts this match' : 'Consecutive knockouts · resets on death';
      }
      if (c.streak.hidden !== (streakCount === 0)) c.streak.hidden = streakCount === 0;
      c.streak.classList.toggle('milestone', streakCount >= 3);
      const progress = race?.players.find((r) => r.slot === p.slot);
      const weaponText = !p.alive ? progress && !over ? 'RESPAWNING' : 'OUT' : p.weapon ? `${p.weapon.name} · ${p.weapon.infinite ? '∞' : p.weapon.ammo}` : 'Fists';
      if (c.weapon.textContent !== weaponText) { c.weapon.textContent = weaponText; c.weapon.title = weaponText; }
      c.weapon.classList.toggle('low', !!p.weapon && !p.weapon.infinite && p.weapon.ammo <= Math.max(1, p.weapon.capacity * 0.2));
      if (progress) {
        const progressText = `Stage ${progress.stage + 1} / ${ARMS_RACE_LADDER.length} · ${progress.stageKills} / ${progress.killsRequired}`;
        if (c.raceProgress.textContent !== progressText) {
          c.raceProgress.textContent = progressText;
          c.raceProgress.style.setProperty('--race-progress', String((progress.stage + progress.stageKills / progress.killsRequired) / ARMS_RACE_LADDER.length));
        }
        c.card.classList.toggle('race-final', progress.stage === ARMS_RACE_LADDER.length - 1);
        if (c.raceStage >= 0 && progress.stage > c.raceStage && p.slot === (online?.localSlot ?? 0)) {
          const def = WEAPON_BY_ID.get(progress.weaponId)?.def;
          showFeedback(progress.nextWeaponId ? `Weapon up! ${def ? weaponDisplayName(def) : progress.weaponId}` : 'Final blade! One kill to win', 'weapon', progress.weaponId);
        }
        c.raceStage = progress.stage;
      }
      const alive = p.alive ? '1' : '0';
      if (c.alive !== alive) c.card.dataset.alive = c.alive = alive;
      const winner = p.slot === winnerSlot ? '1' : '0';
      if (c.winner !== winner) c.card.dataset.winner = c.winner = winner;
      if (p.crown && !c.crown) {
        c.crown = document.createElement('i');
        c.crown.className = 'hud-crown';
        c.face.append(c.crown);
      } else if (!p.crown && c.crown) {
        c.crown.remove();
        c.crown = null;
      }
      // Quantised to whole percent so a slowly ticking burn does not restyle every frame.
      const hpPct = Math.round(Math.max(0, Math.min(1, p.hp / Math.max(1, p.maxHp))) * 100);
      if (c.hpPct !== hpPct) {
        c.hpPct = hpPct;
        c.hp.style.setProperty('--hp', HP_SCALE[hpPct]!);
      }
      if (h.showWins && !race) {
        const w = wins[p.slot] ?? 0;
        const n = Math.min(Math.max(firstTo, w, 1), 10);
        const gained = (hud.lastWins[p.slot] ?? 0) < w;
        if (c.tally.childElementCount !== n || c.wins.textContent !== String(w) || gained) {
          while (c.tally.childElementCount < n) c.tally.append(document.createElement('i'));
          while (c.tally.childElementCount > n) c.tally.lastElementChild?.remove();
          Array.from(c.tally.children).forEach((dot, i) => {
            dot.classList.toggle('on', i < w);
            // The freshly earned pip pops; the class stays until the next change so the animation completes.
            if (gained) dot.classList.toggle('pop', i === w - 1);
          });
          c.wins.textContent = String(w);
        }
      }
    }
    for (let i = 0; i < MAX_PLAYERS; i++) hud.lastWins[i] = wins[i] ?? 0;

    // Between rounds the strip becomes the scorecard: title above, verdict below, winner ringed.
    const cardMode = over ? '1' : '0';
    if (hud.top.dataset.card !== cardMode) {
      hud.top.dataset.card = cardMode;
      if (over) hud.top.dataset.roundOver = '1';
      else delete hud.top.dataset.roundOver;
    }
    const cardKey = over ? `${h.phase}:${winnerSlot}:${h.matchWinner ?? -1}:${inputPresentation.revision}:${settings.controllerGlyphs}:${replayText}` : '';
    if (cardKey !== hud.lastCardKey) {
      hud.lastCardKey = cardKey;
      // Leaving card mode keeps the old text in place: it collapses out of view, no blank box.
      if (over) {
        hud.title.innerHTML = '';
        const winner = plist.find((p) => p.slot === winnerSlot);
        hud.top.style.setProperty('--c', winner?.color ?? '#f4f1ea');
        const small = document.createElement('small');
        small.textContent =
          h.phase === RoundPhase.MatchOver ? 'Match over' : h.phase === RoundPhase.LastKill ? 'Last one standing' : 'Round over';
        const big = document.createElement('b');
        const winnerName = winner && sim && modeHasTeams(sim.ctx.settings.mode) ? `${teamForSlot(sim.ctx.settings, winnerSlot) === 0 ? 'Sun' : 'Moon'} team` : slotName(winnerSlot);
        big.textContent = winner
          ? h.phase === RoundPhase.MatchOver
            ? `${winnerName} wins the ${race ? 'race' : 'match'}`
            : `${winnerName} takes it`
          : h.phase === RoundPhase.MatchOver
            ? 'Match over'
            : 'Everybody dies';
        hud.title.append(small, big);
        hud.sub.textContent =
          h.phase === RoundPhase.MatchOver
            ? `${confirmShortcut} — ${replayText.toLowerCase()} · ${backShortcut} — ${menus.editorPlaytest ? 'edit arena' : 'back to party'}`
            : firstTo
              ? `First to ${firstTo}`
              : 'Next level incoming';
      }
    }

    // Keep a small connection indicator visible, with actionable failures taking precedence.
    const netText = !online
      ? ''
      : online.recovering
        ? 'Round restarted to restore sync — verifying…'
        : online.desync
          ? 'Out of sync — waiting for the host to restart this round…'
          : performance.now() < online.recoveryNoticeUntil
            ? race ? 'Arena restarted to restore sync — weapon progress kept.' : 'Round restarted to restore sync — scores kept.'
        : online.role === 'client' && online.stalledSince && performance.now() - online.stalledSince > 1000
          ? 'Waiting for the host…'
          : `Connected · ${room?.rtt ? `${room.rtt} ms to relay` : 'measuring connection…'}`;
    if (hud.netText !== netText) {
      hud.netText = netText;
      hud.net.textContent = netText;
      hud.net.classList.toggle('show', netText !== '');
    }

    // Debug readout (F3).
    if (hud.dbgShown !== debugHud) {
      hud.dbgShown = debugHud;
      hud.dbg.classList.toggle('hidden', !debugHud);
    }
    // Percentiles over 12 rings and the world hash cost ~0.4 ms; refreshing the readout at 10 Hz keeps
    // the overlay itself from showing up in the numbers it displays.
    if (debugHud && now - hud.dbgAt >= 100) {
      hud.dbgAt = now;
      const players = sim
        ? sim.players().map((e, i) => {
            const slot = e.get(Player)?.slot ?? i;
            const hpNow = e.get(Health)?.hp ?? 0;
            const dead = e.has(Dead) ? ' dead' : '';
            return `P${slot + 1} hp ${hpNow.toFixed(0)}${dead}`;
          })
        : [];
      const perf = profiler.stats();
      const ms = (k: string) => perf[k] ?? { med: 0, p95: 0, p99: 0, max: 0, mean: 0 };
      hud.dbg.textContent = [
        `tick ${h.tick ?? 0}`,
        `hash ${sim?.hash() ?? h.hash ?? '--------'}`,
        `phys ${(h.physicsMs ?? 0).toFixed(2)} ms`,
        `enc  ${(h.gpuMs ?? 0).toFixed(2)} ms`,
        `gpu  ${perf.gpu ? `${perf.gpu.med.toFixed(2)} / p99 ${perf.gpu.p99.toFixed(2)} ms` : 'n/a'}`,
        `ents ${h.entities ?? 0}`,
        `rend ${rendererKind} ${renderer?.canvas.width ?? 0}x${renderer?.canvas.height ?? 0} @${(renderer?.resolutionScale ?? 1).toFixed(2)}`,
        `phase ${h.phase ?? 0}`,
        `frame ${ms('frame').med.toFixed(2)} / p99 ${ms('frame').p99.toFixed(2)} ms  (rAF ${ms('interval').med.toFixed(1)} ms)`,
        `parts ${ms('particles').max | 0}  groups ${ms('groups').max | 0}${stressTarget ? `  stress ${stressTarget}` : ''}`,
        formatStats({ input: ms('input'), sim: ms('sim'), fx: ms('fx'), build: ms('build') }),
        formatStats({ render: ms('render'), hud: ms('hud'), debug: ms('debug') }),
        ...players,
      ].join('\n');
    }
  }

  function bindDebug() {
    type PaneUi = { addBinding: (o: object, k: string) => void; hidden: boolean };
    pane = new Pane({ title: 'Tuning' });
    const ui = pane as unknown as PaneUi;
    ui.addBinding(tuning, 'runSpeed');
    ui.addBinding(tuning, 'jumpSpeed');
    ui.addBinding(tuning, 'gravity');
    ui.hidden = true;
    onWindow('keydown', (e) => {
      if (menus.screen === 'editor' || e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.code === 'F1') debugDraw = !debugDraw;
      if (e.code === 'F2') ui.hidden = !ui.hidden;
      if (e.code === 'F3') debugHud = !debugHud;
      if (e.code === 'F4' && sim) {
        const p = sim.players()[0]?.get(Transform);
        if (p) spawnWeapon(sim.ecs, 'pistol', p.x + 0.4, p.y + 0.6);
      }
      if (e.code === 'F5' && sim) {
        const p = sim.players()[0];
        if (p && !p.has(Dead)) p.set(Health, { hp: 0, maxHp: p.get(Health)?.maxHp ?? 100 });
      }
      if (e.code === 'F6') {
        freezeCam = false;
        tuning.lastKillSlowmo = tuning.lastKillSlowmo < 1 ? 1 : 0.3;
      }
      if (e.code === 'F7') freezeCam = !freezeCam;
      if (e.code === 'F8') void switchRenderer();
      if (e.code === 'F9') recorder.download();
    });
  }

  let gpuAttemptPending = false;
  function adoptGpu(gpuCanvas: HTMLCanvasElement, gpu: Renderer) {
    renderer?.dispose();
    adoptCanvas(gpuCanvas);
    renderer = gpu;
    rendererKind = 'gpu';
    // The normal case needs no label; the notice is for things worth knowing (fallbacks, odd pads).
    if (menus.notice.startsWith('Canvas fallback')) menus.notice = '';
    if (menus.screen !== 'play') show();
  }

  async function tryAdoptGpu(): Promise<boolean> {
    // One attempt at a time: a hung adapter request must not be joined by a pile of clones.
    if (gpuAttemptPending) return false;
    gpuAttemptPending = true;
    const generation = rendererGeneration;
    const gpuCanvas = document.createElement('canvas');
    const gpu = await tryCreateGpuRenderer(gpuCanvas, { lighting: settings.lighting, onDeviceLost: () => {
      if (stopped || generation !== rendererGeneration) return;
      useCanvasRenderer();
      menus.notice = 'Canvas fallback — graphics device disconnected';
      if (menus.screen !== 'play') show();
    } }, 12_000, (late) => {
      if (stopped || generation !== rendererGeneration || settings.renderer === 'canvas') { late.dispose(); return; }
      gpuAttemptPending = false;
      if (rendererKind === 'canvas') adoptGpu(gpuCanvas, late);
      else late.dispose();
    });
    if (stopped || generation !== rendererGeneration) { gpu?.dispose(); return false; }
    if (gpu) {
      gpuAttemptPending = false;
      adoptGpu(gpuCanvas, gpu);
      return true;
    }
    const why = gpuFailureReason();
    menus.notice = why ? `Canvas fallback — WebGPU ${why}` : 'Canvas fallback';
    telemetry.error({ kind: 'gpu-fallback', message: why ?? 'unavailable', source: settings.renderer, context: tracked ? tracked.mode : 'menu', renderer: 'canvas' });
    if (menus.screen !== 'play') show();
    // A timed-out attempt is still in flight and will adopt itself if it ever lands; a hard failure is final.
    if (why !== 'timed out') gpuAttemptPending = false;
    return false;
  }

  async function switchRenderer() {
    if (rendererKind === 'gpu') {
      rendererGeneration++;
      useCanvasRenderer();
      menus.notice = 'Canvas fallback';
    } else if (settings.renderer !== 'canvas') {
      await tryAdoptGpu();
    }
    if (menus.screen !== 'play') show();
  }

  let rendererGeneration = 0;
  let configuredRenderer = settings.renderer;
  let configuredLighting = settings.lighting;
  function useCanvasRenderer() {
    const c = document.createElement('canvas');
    const next = createCanvasRenderer(c);
    renderer?.dispose();
    renderer = next;
    adoptCanvas(c);
    rendererKind = 'canvas';
    viewDirty = true;
  }

  async function applyVideoSettings() {
    if (configuredRenderer === settings.renderer && configuredLighting === settings.lighting) return;
    configuredRenderer = settings.renderer;
    configuredLighting = settings.lighting;
    rendererGeneration++;
    gpuAttemptPending = false;
    useCanvasRenderer();
    if (settings.renderer !== 'canvas') await tryAdoptGpu();
    else if (menus.notice.startsWith('Canvas fallback')) menus.notice = '';
  }

  return {
    async start() {
      // An invite link (?room=CODE) opens the lobby with the code filled in; joining still takes a
      // click so the browser has the user gesture audio needs.
      const linkedRoom = normalizeRoomCode(new URLSearchParams(location.search).get('room') ?? '');
      if (isRoomCode(linkedRoom) && menus.screen === 'menu') {
        menus.roomCode = linkedRoom;
        menus.screen = 'lobby';
      }
      show();
      telemetry.page({
        renderer: settings.renderer,
        entry: isRoomCode(linkedRoom) ? 'invite' : 'direct',
        standalone: window.matchMedia?.('(display-mode: standalone)').matches ?? false,
        width: window.innerWidth,
        height: window.innerHeight,
        dpr: Math.round(window.devicePixelRatio * 100) / 100,
        cores: navigator.hardwareConcurrency ?? 0,
        memory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 0,
        touch: (navigator.maxTouchPoints ?? 0) > 0,
        gamepads: pollGamepads().filter(Boolean).length,
      });
      // Closing the tab mid-match still yields an end row (with how long it ran) before the last batch goes out.
      telemetry.onPageHide(() => trackMatchEnd('unload'));
      onWindow('gamepadconnected', (ev) => {
        const pad = (ev as GamepadEvent).gamepad ?? pollGamepads().find(Boolean);
        if (!pad) return;
        if (pad.mapping && pad.mapping !== 'standard' && !maps[pad.id]) {
          menus.notice = `Non-standard pad “${pad.id}” — open Settings to remap.`;
        }
        const key = padKey(pad);
        if (sim && paused) {
          // The browser usually hands a returning pad its old index; if not, the fighter whose pad
          // vanished takes the newcomer.
          const connected = new Set(pollGamepads().filter(Boolean).map((p) => padKey(p!)));
          const orphan = slotDevices.findIndex((d) => isPadKey(d) && !connected.has(d));
          if (!slotDevices.includes(key) && orphan >= 0) {
            const old = slotDevices[orphan]!;
            slotDevices[orphan] = key;
            const seat = menus.seats.find((s) => s.padId === old);
            if (seat) seat.padId = key;
          }
          if (menus.screen === 'disconnect' && !missingSeatedPad()) setPaused(false);
          return;
        }
        if (menus.screen === 'join') {
          takeSeat(menus.seats, key, padLabel(pad));
          show();
        }
      });
      onWindow('gamepaddisconnected', (ev) => {
        const pad = (ev as GamepadEvent).gamepad;
        if (pad) { padEdges[pad.index]?.reset(); delete lastAim[pad.index]; }
        // Only a pad that is actually steering a fighter interrupts the match.
        const inMatch = !pad || slotDevices.includes(padKey(pad)) || (!slotDevices.length && pad.index < humanCount);
        if (sim && inMatch && !matchIsOver() && (menus.screen === 'play' || menus.screen === 'pause')) {
          setPaused(true);
          menus.screen = 'disconnect';
          show();
        }
      });
      const isTextField = (el: Element | null) =>
        el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && !['checkbox', 'range', 'number', 'button'].includes(el.type));
      onWindow('keydown', (e) => {
        if (e.repeat) return;
        useKeyboardHints();
        if (e.code === 'Enter' && menus.screen === 'play' && e.target instanceof HTMLButtonElement) return;
        if (e.code === 'Enter' && menus.screen === 'play' && practice?.step === 5) { hud.practiceStart.click(); return; }
        if (e.code === 'Escape' && matchIsOver()) {
          e.preventDefault();
          keys.takePause();
          leaveFinishedMatch();
          return;
        }
        // Gameplay pause is owned by the input latch. Menu-back must not consume the same event.
        if (e.code === 'Escape' && sim && (menus.screen === 'pause' || menus.screen === 'disconnect')) return;
        if ((e.code === 'ArrowLeft' || e.code === 'ArrowRight') && matchIsOver()) {
          e.preventDefault(); cycleArenaVote(e.code === 'ArrowRight' ? 1 : -1, 'keyboard'); return;
        }
        if (e.code === 'Enter' && matchIsOver()) {
          e.preventDefault();
          if (finale.active) finale.skip();
          else rematch();
          return;
        }
        if (menus.screen === 'join') {
          lastMenuDevice = 'keyboard';
          if (e.target instanceof HTMLButtonElement) {
            // Focused mouse/Tab controls keep native activation, without also taking a seat
            // or starting the match through the older keyboard shortcuts.
            if (e.code === 'Enter' || e.code === 'Space') return;
            const dir: NavDir | null = e.code === 'ArrowLeft' ? 'left' : e.code === 'ArrowRight' ? 'right' : e.code === 'ArrowUp' ? 'up' : e.code === 'ArrowDown' ? 'down' : null;
            if (dir) { e.preventDefault(); navigateMenu(dir, false, false); return; }
          }
          if (e.code === 'KeyC') {
            const seat = menus.seats.findIndex((s) => s.taken && s.padId === 'keyboard');
            openCharacter(seat);
            return;
          }
          if (e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
            const seat = menus.seats.find((s) => s.taken && s.padId === 'keyboard') ?? [...menus.seats].reverse().find((s) => s.taken);
            if (seat?.taken) {
              cycleSeatColor(seat, e.code === 'ArrowRight' ? 1 : -1, menus.seats);
              show();
            }
            return;
          }
          if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
            menus.bots = Math.max(0, Math.min(maxBots(menus.seats), menus.bots + (e.code === 'ArrowUp' ? 1 : -1)));
            show();
            return;
          }
          if (e.code === 'Space' || e.code === 'KeyA') {
            takeOrReadySeat(menus.seats, 'keyboard', 'Keyboard');
            show();
            return;
          }
          if (e.code === 'Enter') {
            startIfReady();
            return;
          }
          if (e.code === 'Escape') {
            keys.takePause();
            backOut();
            return;
          }
          return;
        }
        if (NAV_SCREENS.has(menus.screen)) {
          lastMenuDevice = 'keyboard';
          const active = document.activeElement;
          // Arrow keys walk the menu unless a field that uses them natively (text, select) has focus.
          const dir: NavDir | null =
            e.code === 'ArrowUp' ? 'up' : e.code === 'ArrowDown' ? 'down' : e.code === 'ArrowLeft' ? 'left' : e.code === 'ArrowRight' ? 'right' : null;
          if (dir && !isTextField(active) && !(active instanceof HTMLSelectElement)) {
            e.preventDefault();
            navigateMenu(dir, false, false);
            return;
          }
          // Enter with nothing focused takes the screen's primary action (a focused button already clicks itself).
          if (e.code === 'Enter' && !menusEl.contains(active)) navigateMenu(null, true, false);
          if (e.code === 'Escape' && menus.screen !== 'menu') { keys.takePause(); backOut(); }
        }
      });
      const loseFocus = () => {
        keys.clear();
        latches.forEach(consumeLatch);
        online?.sender?.neutral(performance.now());
        if (sim && menus.screen === 'play' && !matchIsOver()) setPaused(true);
      };
      onWindow('blur', loseFocus);
      document.addEventListener('visibilitychange', () => { if (document.hidden) loseFocus(); }, { signal: lifecycle.signal });
      // Fresh pointer/keyboard input updates help without replacing the focused controls.
      onWindow('pointerdown', () => {
        useKeyboardHints();
        for (const el of menusEl.querySelectorAll('.pad-focus')) el.classList.remove('pad-focus');
      });
      let pointerPosition: { x: number; y: number } | null = null;
      onWindow('pointermove', (event) => {
        if (pointerPosition && Math.hypot(event.clientX - pointerPosition.x, event.clientY - pointerPosition.y) < 4) return;
        pointerPosition = { x: event.clientX, y: event.clientY };
        useKeyboardHints();
      });
      menusEl.addEventListener('change', () => syncControlHints(true), { signal: lifecycle.signal });
      renderer = createCanvasRenderer(canvas);
      bindDebug();
      // Opening the audio device costs ~200 ms on the main thread; do it behind the title screen
      // (once the first frames are up) rather than on the first frame of the match.
      warmTimer = window.setTimeout(() => mixer.warm(), 1000);
      if ('serviceWorker' in navigator) {
        if (import.meta.env.PROD) void navigator.serviceWorker.register('/sw.js');
        else void navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => void r.unregister()));
      }
      raf = requestAnimationFrame(tick);
      if (settings.renderer !== 'canvas') {
        void tryAdoptGpu()
          .then((ok) => {
            if (ok && menus.screen !== 'play') show();
          })
          .catch(() => undefined);
      }
    },
    stop() {
      stopped = true;
      rendererGeneration++;
      cancelAnimationFrame(raf);
      clearTimeout(warmTimer);
      lifecycle.abort();
      sizeObserver?.disconnect();
      keys.dispose();
      trackMatchEnd('unload');
      teardownRoom();
      stopDemo();
      editorView?.dispose();
      editorView = null;
      sim?.destroy();
      sim = null;
      renderer?.dispose();
      renderer = null;
      pane?.dispose();
      mixer.dispose();
      telemetry.destroy();
    },
  };
}
